// import React, {
//   useState,
//   useEffect,
//   useRef,
//   useCallback,
//   useMemo,
// } from "react";
// import { Link, useParams, useNavigate, useLocation } from "react-router-dom";
// import { toast } from "react-toastify";
// import cryptoRandomString from "crypto-random-string";
// import { Card, Spin, Row, Typography } from "antd";
// import { FileTextOutlined } from "@ant-design/icons";
// import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
// import dayjs from "dayjs";

// // Components
// import CustomerDetailsSection from "./CustomerDetailsSection";
// import PlanDetailsSection from "./PlanDetailsSection";
// import PaymentDetailsSection from "./PaymentDetailsSection";
// import AttorneyNumbersSection from "./AttorneyNumbersSection";
// import OrderSummary from "./OrderSummary";
// import AddCustomerModal from "../AddCustomerModal";

// // Hooks and Services
// import {
//   useOrderForm,
//   useOrderCalculations,
//   useAttorneyValidation,
// } from "../../hooks/useOrderForm";
// import {
//   fetchStates,
//   loadCustomers,
//   loadPlans,
//   addCustomer,
//   createOrder,
//   fetchPlansByAttorneyType,
//   fetchSingleOrderDetails,
//   fetchAttorneyTMCounts,
//   fetchPaymentModes,
//   fetchBanks,
// } from "../../services/orderService";

// const { Title } = Typography;

// const CreateOrder = () => {
//   const location = useLocation();
//   const navigate = useNavigate();
//   const queryClient = useQueryClient();
//   const { order_id, user_id } = useParams();
//   const isEditing = Boolean(order_id && user_id);
//   const formRef = useRef(null);
//   const isTMCountManuallySet = useRef(false);

//   // Custom Hooks
//   const {
//     formData,
//     setFormData,
//     orderSummary,
//     setOrderSummary,
//     isEditingAmount,
//     setIsEditingAmount,
//     editablePlanAmount,
//     setEditablePlanAmount,
//     isSubmitting,
//     setIsSubmitting,
//     isCheckingAttorney,
//     setIsCheckingAttorney,
//     isGenerating,
//     setIsGenerating,
//     errors,
//     setErrors,
//     customerErrors,
//     setCustomerErrors,
//     attorneyCount,
//     setAttorneyCount,
//     attorneyNumbers,
//     setAttorneyNumbers,
//     attorneyValidation,
//     setAttorneyValidation,
//     attorneyValidationMessages,
//     setAttorneyValidationMessages,
//     attorneyTMCounts,
//     setAttorneyTMCounts,
//   } = useOrderForm();

//   // Local States
//   const [newCustomer, setNewCustomer] = useState({
//     name: "",
//     email: "",
//     mobile: "",
//     password: "",
//     address: "",
//   });
//   const [password, setPassword] = useState("");
//   const [openAddCustomer, setOpenAddCustomer] = useState(false);

//   // Token
//   const [token] = useState(localStorage.getItem("cms_token"));

//   // Validation Patterns
//   const nameRegex = /^[A-Za-z\s]{3,50}$/;
//   const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
//   const mobileRegex = /^[6-9]\d{9}$/;

//   // ==================== TANSTACK QUERY - DATA FETCHING ====================

//   // Fetch States
//   const { data: statesData = [], isLoading: statesLoading } = useQuery({
//     queryKey: ["states"],
//     queryFn: fetchStates,
//     staleTime: 5 * 60 * 1000, // 5 minutes
//   });

//   // Fetch Customers
//   const { data: customersData = [], isLoading: customersLoading } = useQuery({
//     queryKey: ["customers"],
//     queryFn: loadCustomers,
//     staleTime: 2 * 60 * 1000, // 2 minutes
//     refetchOnMount: "always",
//   });

//   // Fetch Plans (initial load)
//   const { data: plansData = [], isLoading: plansLoading } = useQuery({
//     queryKey: ["plans", formData.is_attorney],
//     queryFn: () => fetchPlansByAttorneyType("0"),
//     enabled: formData.is_attorney === "0",
//     staleTime: 5 * 60 * 1000,
//     refetchOnMount: true,
//   });

//   // Fetch Plans by Attorney Type (when plan type changes)
//   const { data: attorneyPlansData = [], isLoading: attorneyPlansLoading } =
//     useQuery({
//       queryKey: ["attorneyPlans", formData.is_attorney],
//       queryFn: () => fetchPlansByAttorneyType("1"),
//       enabled: formData.is_attorney == "1",
//       staleTime: 5 * 60 * 1000,
//       refetchOnMount: true,
//     });

//   // Fetch Payment Modes
//   const { data: paymentModes = [] } = useQuery({
//     queryKey: ["paymentModes"],
//     queryFn: fetchPaymentModes,
//     staleTime: 10 * 60 * 1000,
//   });

//   // Fetch Banks
//   const { data: bankData = [], isLoading: banksLoading } = useQuery({
//     queryKey: ["bank-master"],
//     queryFn: fetchBanks,
//     staleTime: 1000 * 60 * 60, // 1 hour
//   });

//   const bankOptions = useMemo(() => {
//     if (!Array.isArray(bankData)) return [];
//     return bankData.map((bank) => ({
//       value: bank.label,
//       label: bank.label,
//     }));
//   }, [bankData]);

//   // Fetch Single Order Details (Edit Mode)
//   const {
//     data: editOrderData,
//     isLoading: isLoadingOrderDetails,
//     refetch: refetchOrderDetails,
//   } = useQuery({
//     queryKey: ["orderDetails", order_id, user_id],
//     queryFn: () => fetchSingleOrderDetails(order_id, user_id),
//     enabled: isEditing,
//     staleTime: 0,
//     cacheTime: 0,
//     refetchOnMount: "always",
//   });

//   // Combined loading state
//   const isPageLoading = statesLoading || customersLoading || plansLoading;

//   // ==================== TANSTACK QUERY - MUTATIONS ====================

//   // Mutation: Add Customer
//   const addCustomerMutation = useMutation({
//     mutationFn: addCustomer,
//     onSuccess: (response) => {
//       if (response.status === 200 || response.success) {
//         toast.success(response.message || "Customer added successfully!");

//         // Invalidate and refetch customers
//         queryClient.invalidateQueries({ queryKey: ["customers"] });

//         const newCustomerId = response.data?.user_id;
//         const newCustomerName = response.data?.name;
//         const newCustomerEmail = response.data?.email;
//         const newCustomerMobile = response.data?.mobile;

//         setFormData((prev) => ({
//           ...prev,
//           user_id: newCustomerId,
//         }));

//         setOrderSummary((prev) => ({
//           ...prev,
//           customerName: newCustomerName || "-",
//           customerEmail: newCustomerEmail || "-",
//           customerMobile: newCustomerMobile || "-",
//         }));

//         setNewCustomer({
//           name: "",
//           email: "",
//           mobile: "",
//           password: "",
//           address: "",
//         });
//         setPassword("");
//         setCustomerErrors({});
//         setOpenAddCustomer(false);
//       } else {
//         if (response.status === 422 && response.errors) {
//           const formattedErrors = {};
//           Object.entries(response.errors).forEach(([field, messages]) => {
//             formattedErrors[field] = Array.isArray(messages)
//               ? messages[0]
//               : messages;
//           });
//           setCustomerErrors(formattedErrors);
//           toast.warning("Please correct the highlighted fields.");
//         } else {
//           toast.error(response.message || "Failed to add customer.");
//         }
//       }
//     },
//     onError: (error) => {
//       console.error("Error adding customer:", error);
//       const serverErrors = error?.response?.data?.errors;
//       if (serverErrors) {
//         const formattedErrors = {};
//         Object.entries(serverErrors).forEach(([field, messages]) => {
//           formattedErrors[field] = Array.isArray(messages)
//             ? messages[0]
//             : messages;
//         });
//         setCustomerErrors(formattedErrors);
//         toast.warning("Please correct the highlighted fields.");
//       } else {
//         toast.error(
//           error?.response?.data?.message ||
//             "Something went wrong while adding customer.",
//         );
//       }
//     },
//   });

//   // Mutation: Create/Update Order
//   const createOrderMutation = useMutation({
//     mutationFn: (payload) => createOrder(payload, token),
//     onSuccess: (response) => {
//       if (response.status === 200 || response.success) {
//         toast.success(
//           isEditing
//             ? "Subscription updated successfully!"
//             : "Subscription created successfully!",
//         );

//         // Invalidate orders list
//         queryClient.invalidateQueries({ queryKey: ["orders"] });

//         navigate("/cms/manage-customer/manage-order");
//       } else {
//         toast.warning(
//           response.message ||
//             `Failed to ${isEditing ? "update" : "create"} subscription.`,
//         );
//       }
//     },
//     onError: (error) => {
//       console.error("Error submitting subscription:", error);
//       toast.error("Something went wrong while submitting the subscription.");
//     },
//   });

//   // ==================== HELPER FUNCTIONS ====================

//   const normalizeAttorneyTMResponse = (apiData) => {
//     const tmCounts = {};
//     const validation = {};
//     const messages = {};

//     apiData.details.forEach((item) => {
//       const key = String(item.attorney_number);
//       tmCounts[key] = Number(item.count || 0);
//       validation[key] = "valid";
//       messages[key] = `Valid - ${item.count} TMs`;
//     });

//     return { tmCounts, validation, messages };
//   };

//   // useEffect that auto-selects customer:
//   useEffect(() => {
//     if (!customersData.length) return;

//     const params = new URLSearchParams(location.search);
//     const customerId = params.get("customer_id");

//     if (!customerId) return;

//     const customer = customersData.find((c) => c.id === Number(customerId));
//     if (!customer) return;

//     setFormData((prev) => ({
//       ...prev,
//       user_id: Number(customerId),
//     }));

//     setOrderSummary((prev) => ({
//       ...prev,
//       customerName: customer.name || "-",
//       customerEmail: customer.email || "-",
//       customerMobile: customer.mobile || "-",
//     }));
//   }, [customersData, location.search]);

//   const isCustomerPreSelected = Boolean(
//     new URLSearchParams(location.search).get("customer_id"),
//   );

//   const activePlans = useMemo(() => {
//     if (formData.is_attorney === "1") {
//       const plans = Array.isArray(attorneyPlansData?.data)
//         ? attorneyPlansData.data
//         : [];
//       return plans;
//     }

//     if (formData.is_attorney === "0") {
//       const plans = Array.isArray(plansData?.data) ? plansData.data : [];
//       return plans;
//     }
//     return [];
//   }, [formData.is_attorney, attorneyPlansData, plansData]);

//   // Calculations Hook
//   const { calculateOrderSummary, safeNumber } = useOrderCalculations(
//     formData,
//     activePlans,
//     customersData,
//     isEditingAmount,
//     editablePlanAmount,
//     attorneyNumbers,
//     attorneyTMCounts,
//     attorneyValidation,
//   );

//   // Attorney Validation Hook
//   const { handleCheckAttorneyStatus } = useAttorneyValidation(
//     setIsCheckingAttorney,
//     setAttorneyValidation,
//     setAttorneyValidationMessages,
//     setAttorneyTMCounts,
//     calculateOrderSummary,
//   );

//   // ==================== UTILITY FUNCTIONS ====================

//   const showError = (field, message) => {
//     toast.warning(message);
//     setErrors((prev) => ({ ...prev, [field]: message }));
//   };

//   const formatPrice = (price) =>
//     Number(price || 0).toLocaleString("en-IN", {
//       style: "currency",
//       currency: "INR",
//     });

//   // ==================== ATTORNEY TM COUNT HANDLER ====================

//   const handleCheckAttorneyTMCounts = async () => {
//     isTMCountManuallySet.current = false;
//     const attorneys = attorneyNumbers.filter((n) => n.trim());

//     if (!attorneys.length) {
//       toast.warning("Please enter at least one attorney number");
//       return;
//     }

//     try {
//       setIsCheckingAttorney(true);

//       const res = await fetchAttorneyTMCounts(attorneys);

//       if (res.status === 200 && res.data?.details) {
//         const { tmCounts, validation, messages } = normalizeAttorneyTMResponse(
//           res.data,
//         );

//         setAttorneyTMCounts(tmCounts);
//         setAttorneyValidation(validation);
//         setAttorneyValidationMessages(messages);

//         toast.success("TM counts fetched successfully");
//       } else {
//         toast.warning("No TM data found for provided attorneys");
//       }
//     } catch (err) {
//       console.error("TM count fetch failed", err);
//       toast.error("Failed to fetch TM counts");
//     } finally {
//       setIsCheckingAttorney(false);
//     }
//   };

//   // Combined handler for Create/Edit mode
//   const handleAttorneyCheckClick = () => {
//     isTMCountManuallySet.current = false;
//     if (isEditing) {
//       handleCheckAttorneyTMCounts();
//     } else {
//       handleCheckAttorneyStatus(attorneyNumbers);
//     }
//   };

//   // useEffect(() => {
//   //   if (!isEditing || !editOrderData?.data) return;

//   //   const data = editOrderData.data;
//   //   const isAttorney = String(data.is_attorney);

//   //   // First, trigger plan loading
//   //   setFormData((prev) => ({
//   //     ...prev,
//   //     is_attorney: isAttorney,
//   //   }));
//   // }, [isEditing, editOrderData?.data]);

//   // ==================== EDIT MODE - POPULATE FORM DATA ====================

//   useEffect(() => {
//     if (!isEditing || !editOrderData?.data) return;

//     const data = editOrderData.data;
//     const isAttorney = String(data.is_attorney);

//     const populateEditData = async () => {
//       const attorneys = editOrderData.attorney_number || [];
//       const customerType = String(data.customer_type);
//       const gstInclusion = data.gst_inclusion === 1;

//       // console.log("📝 Populating edit form with:", data);

//       let planType = null;
//       if (customerType === "2") {
//         planType = null;
//       } else if (data.plan_type && data.plan_type !== "free_trial") {
//         planType = data.plan_type;
//       }

//       // Populate form data
//       setFormData((prev) => ({
//         ...prev,
//         user_id: data.user_id,
//         plan_id: Number(data.plan_id),
//         customer_type: customerType,
//         plan_type: planType,
//         is_attorney: isAttorney,
//         end_date: data.subscription_end_date || "",
//         gst_inclusion: gstInclusion,
//         plan_price: Number(data.plan_price),
//         total_tm_count: Number(data.total_tm_count),
//         payment_status: data.payment_status === "1" ? "paid" : "unpaid",
//         payment_mode: data.payment_mode || "",
//         amount_received: data.amount_received || "",
//         bank_name: data.bank_name || "",
//         utr_no: data.utr_no || "",
//         payment_attachment: null,
//       }));

//       // Calculate duration for edit mode (free trial)
//       if (customerType === "2" && data.subscription_end_date) {
//         const endDate = dayjs(data.subscription_end_date, "DD-MM-YYYY");
//         const today = dayjs().startOf("day");
//         const days = endDate.diff(today, "day");
//         setOrderSummary((prev) => ({
//           ...prev,
//           trialDuration: days > 0 ? days : 0,
//         }));
//       }

//       // Handle attorney numbers
//       if (
//         Array.isArray(attorneys) &&
//         attorneys.length > 0 &&
//         isAttorney === "1"
//       ) {
//         setAttorneyNumbers(attorneys);
//         setAttorneyCount(attorneys.length);

//         try {
//           const tmResponse = await fetchAttorneyTMCounts(attorneys);

//           if (tmResponse.status === 200 && tmResponse.data?.details) {
//             const { tmCounts, validation, messages } =
//               normalizeAttorneyTMResponse(tmResponse.data);

//             setAttorneyTMCounts(tmCounts);
//             setAttorneyValidation(validation);
//             setAttorneyValidationMessages(messages);
//           }
//         } catch (error) {
//           console.error("Error fetching attorney TM counts:", error);

//           // Fallback
//           const perAttorneyTM = Math.floor(
//             data.total_tm_count / attorneys.length,
//           );
//           const tmCounts = {};
//           const validation = {};
//           const messages = {};

//           attorneys.forEach((num) => {
//             tmCounts[num] = perAttorneyTM;
//             validation[num] = "pending";
//             messages[num] = "Click 'Check Status' to verify";
//           });

//           setAttorneyTMCounts(tmCounts);
//           setAttorneyValidation(validation);
//           setAttorneyValidationMessages(messages);
//         }
//       } else if (isAttorney === "0") {
//         setAttorneyCount(1);
//         setAttorneyNumbers([""]);
//         setAttorneyTMCounts({ "": 1 });
//       }

//       setEditablePlanAmount(Number(data.plan_price));
//       setIsEditingAmount(false);
//     };

//     populateEditData();
//   }, [
//     isEditing,
//     editOrderData?.data,
//     attorneyPlansLoading,
//     plansLoading,
//     // attorneyPlansData?.data,
//     // plansData?.data,
//   ]);

//   // ==================== CALCULATIONS ====================

//   useEffect(() => {
//     if (customersData.length === 0) return;

//     if (formData.customer_type == 2) {
//       const summary = calculateOrderSummary();

//       const finalTMCount = isTMCountManuallySet.current
//         ? orderSummary.totalTMCount
//         : summary.totalTMCount;

//       let trialDuration = null;
//       if (formData.end_date) {
//         const days = dayjs(formData.end_date, "DD-MM-YYYY").diff(
//           dayjs().startOf("day"),
//           "day",
//         );
//         trialDuration = days > 0 ? days : 0;
//       }

//       setOrderSummary((prev) => ({
//         ...summary,
//         trialDuration: trialDuration ?? prev.trialDuration,
//         totalTMCount: finalTMCount,
//       }));
//       setFormData((prev) => ({
//         ...prev,
//         total_amount: 0,
//         final_amount: 0,
//         total_tm_count: finalTMCount,
//       }));
//       return;
//     }

//     // Wait for plans to load
//     if (
//       formData.is_attorney === "1" &&
//       (!attorneyPlansData || attorneyPlansData.length === 0)
//     ) {
//       return;
//     }

//     if (
//       formData.is_attorney === "0" &&
//       (!plansData || plansData.length === 0)
//     ) {
//       return;
//     }

//     if (formData.customer_type == 1) {
//       if (!formData.plan_id || !formData.plan_type) {
//         setOrderSummary((prev) => ({
//           ...prev,
//           planAmount: 0,
//           finalAmount: 0,
//           totalAmount: 0,
//           totalTMCount: formData.is_attorney === "0" ? 1 : 0,
//           gstAmount: 0,
//         }));

//         setFormData((prev) => ({
//           ...prev,
//           total_amount: 0,
//           final_amount: 0,
//           total_tm_count: formData.is_attorney === "0" ? 1 : 0,
//         }));

//         return;
//       }
//     }

//     const summary = calculateOrderSummary();

//     // If TM count or plan amount was manually set, override calculations
//     const finalTMCount = isTMCountManuallySet.current
//       ? orderSummary.totalTMCount
//       : summary.totalTMCount;

//     const finalPlanAmount = isEditingAmount
//       ? editablePlanAmount
//       : Number(summary.planAmount);

//     // Recalculate with manual values
//     const finalAmount = finalPlanAmount * finalTMCount;
//     const gstAmount = summary.includeGST ? finalAmount * 0.18 : 0;
//     const totalAmount = finalAmount + gstAmount;

//     // setOrderSummary(summary);
//     // setOrderSummary(
//     //   isTMCountManuallySet.current
//     //     ? { ...summary, totalTMCount: orderSummary.totalTMCount }
//     //     : summary,
//     // );

//     setOrderSummary(
//       isTMCountManuallySet.current
//         ? {
//             ...summary,
//             totalTMCount: finalTMCount,
//             planAmount: Number(finalPlanAmount).toFixed(2),
//             finalAmount: Number(finalAmount).toFixed(2),
//             gstAmount: Number(gstAmount).toFixed(2),
//             totalAmount: Number(totalAmount).toFixed(2),
//           }
//         : summary,
//     );

//     // setFormData((prev) => ({
//     //   ...prev,
//     //   total_amount: summary.planAmount,
//     //   final_amount: summary.totalAmount,
//     //   // total_tm_count: summary.totalTMCount,
//     //   total_tm_count: isTMCountManuallySet.current
//     //     ? prev.total_tm_count
//     //     : summary.totalTMCount,
//     // }));

//     setFormData((prev) => ({
//       ...prev,
//       total_amount: isTMCountManuallySet.current
//         ? finalPlanAmount
//         : summary.planAmount,
//       final_amount: isTMCountManuallySet.current
//         ? totalAmount
//         : summary.totalAmount,
//       total_tm_count: finalTMCount,
//     }));
//   }, [
//     customersData,
//     plansData,
//     attorneyPlansData,
//     formData.plan_id,
//     formData.plan_type,
//     formData.customer_type,
//     formData.user_id,
//     formData.end_date,
//     formData.gst_inclusion,
//     formData.plan_price,
//     formData.is_attorney,
//     editablePlanAmount,
//     isEditingAmount,
//     attorneyTMCounts,
//     attorneyValidation,
//   ]);

//   // console.log("when-editing", formData);

//   // ==================== ATTORNEY HANDLING ====================

//   const handleAttorneyNumberChange = (index, value) => {
//     const sanitized = value.replace(/\D/g, "");

//     setAttorneyNumbers((prev) => {
//       const updated = [...prev];

//       if (!sanitized) {
//         updated[index] = "";
//         return updated;
//       }

//       const isDuplicate = prev.some(
//         (num, i) => i !== index && num === sanitized,
//       );

//       if (isDuplicate) {
//         toast.warning("Attorney number must be unique");

//         setAttorneyValidation((prevVal) => ({
//           ...prevVal,
//           [sanitized]: "invalid",
//         }));

//         setAttorneyValidationMessages((prevMsg) => ({
//           ...prevMsg,
//           [sanitized]: "Duplicate attorney number",
//         }));

//         return prev;
//       }

//       updated[index] = sanitized;

//       setAttorneyValidation((prevVal) => {
//         const copy = { ...prevVal };
//         delete copy[sanitized];
//         return copy;
//       });

//       setAttorneyValidationMessages((prevMsg) => {
//         const copy = { ...prevMsg };
//         delete copy[sanitized];
//         return copy;
//       });

//       return updated;
//     });
//   };

//   // ==================== FORM HANDLERS ====================

//   const handleInputChange = useCallback((event, fieldName = null) => {
//     let name, value;

//     if (fieldName) {
//       name = fieldName;
//       value = event?.value || "";
//     } else {
//       name = event.target.name;
//       value = event.target.value;
//     }

//     setFormData((prevData) => ({
//       ...prevData,
//       [name]: value,
//     }));

//     setErrors((prev) => ({ ...prev, [name]: "" }));
//   }, []);

//   const handleCustomerTypeChange = (option) => {
//     if (!formData.user_id) {
//       showError("user_id", "Please select Customer Name first.");
//       return;
//     }

//     const selectedType = option ? option.value : "";

//     // COMPLETE RESET
//     setFormData((prev) => ({
//       ...prev,
//       is_attorney: selectedType,
//       plan_id: null,
//       plan_type: null,
//       plan_price: 0,
//       total_amount: 0,
//       final_amount: 0,
//       total_tm_count: 0,
//       end_date: "",
//     }));

//     // RESET ATTORNEY STATE
//     if (selectedType === "0") {
//       // Corporates - set default values
//       setAttorneyCount(1);
//       setAttorneyNumbers([""]);
//       setAttorneyTMCounts({ "": 1 });
//       setAttorneyValidation({});
//       setAttorneyValidationMessages({});
//     } else {
//       // Attorney - complete reset
//       setAttorneyCount(0);
//       setAttorneyNumbers([]);
//       setAttorneyTMCounts({});
//       setAttorneyValidation({});
//       setAttorneyValidationMessages({});
//     }

//     // RESET EDITABLE AMOUNT
//     setEditablePlanAmount(0);
//     setIsEditingAmount(false);

//     // RESET ORDER SUMMARY COMPLETELY
//     setOrderSummary((prev) => ({
//       ...prev,
//       customerName: prev.customerName,
//       customerEmail: prev.customerEmail,
//       customerMobile: prev.customerMobile,
//       customerType: prev.customerType,
//       // customerType:
//       //   formData.customer_type === "1"
//       //     ? "Paid"
//       //     : formData.customer_type === "2"
//       //       ? "Free Trial"
//       //       : prev.customerType,

//       planName: "-",
//       planType: "-",
//       planAmount: 0,
//       totalTMCount: selectedType === "0" ? 1 : 0,
//       finalAmount: 0,
//       gstAmount: 0,
//       totalAmount: 0,
//       includeGST: prev.includeGST,
//       attorneyDetails: [],
//       // end_date: prev.end_date,
//       end_date: "",
//     }));

//     setErrors((prev) => ({ ...prev, is_attorney: "" }));
//   };

//   // Handler for Paid/Free Trial selection
//   const handleCustomerPaymentTypeChange = (option) => {
//     if (!formData.user_id) {
//       showError("user_id", "Please select Customer Name first.");
//       return;
//     }

//     const selectedPaymentType = option ? option.value : "";

//     setFormData((prev) => ({
//       ...prev,
//       customer_type: selectedPaymentType,
//       end_date: selectedPaymentType === "2" ? prev.end_date : "",
//     }));

//     // Reset attorney state for Free Trial
//     // if (selectedPaymentType === "2") {
//     //   setAttorneyCount(0);
//     //   setAttorneyNumbers([]);
//     //   setAttorneyTMCounts({});
//     //   setAttorneyValidation({});
//     //   setAttorneyValidationMessages({});
//     // }

//     setOrderSummary((prev) => ({
//       ...prev,
//       customerType:
//         selectedPaymentType === "1"
//           ? "Paid"
//           : selectedPaymentType === "2"
//             ? "Free Trial"
//             : "-",
//       end_date: selectedPaymentType === "2" ? prev.end_date : "-",
//     }));

//     setErrors((prev) => ({ ...prev, customer_type: "" }));
//   };

//   const handlePlanTypeChange = useCallback(
//     (selectedOption) => {
//       if (!formData.user_id) {
//         showError("user_id", "Please select Customer Name first.");
//         return;
//       }
//       if (!formData.is_attorney && formData.is_attorney !== "0") {
//         showError("is_attorney", "Please select Plan Type first.");
//         return;
//       }
//       if (!formData.plan_id) {
//         showError("plan_id", "Please select Plan Name first.");
//         return;
//       }

//       const selectedType = selectedOption?.value || "";

//       const selectedPlan = activePlans.find(
//         (plan) => plan.id === Number(formData.plan_id),
//       );

//       if (selectedPlan) {
//         const amount =
//           selectedType === "monthly"
//             ? Number(selectedPlan.monthly_price)
//             : Number(selectedPlan.annual_price);

//         setFormData((prev) => ({
//           ...prev,
//           plan_type: selectedType,
//           plan_price: amount,
//           end_date: "",
//         }));

//         setEditablePlanAmount(amount);
//         setIsEditingAmount(false);

//         setOrderSummary((prev) => ({
//           ...prev,
//           // customerType: formData.customer_type === "1" ? "Paid" : "Free Trial",
//           planType: selectedType === "monthly" ? "Monthly" : "Annual",
//           planAmount: amount.toFixed(2),
//           end_date: "_",
//         }));

//         setErrors((prev) => ({ ...prev, plan_type: "" }));

//         toast.success(
//           `Plan amount updated to ₹${amount.toLocaleString("en-IN")}`,
//         );
//       }
//     },
//     [formData.user_id, formData.is_attorney, formData.plan_id, activePlans],
//   );

//   const handleSelect = (fieldName, selectedOption) => {
//     // ==================== CUSTOMER SELECTION ====================
//     if (fieldName === "user_id") {
//       const value = selectedOption?.value || null;
//       const selectedCustomer = customersData.find(
//         (c) => c.id === Number(value),
//       );

//       // Reset all dependent fields when customer changes
//       setFormData((prev) => ({
//         ...prev,
//         user_id: value,
//         is_attorney: null,
//         plan_id: null,
//         plan_type: null,
//         plan_price: 0,
//         total_amount: 0,
//         final_amount: 0,
//         total_tm_count: 0,
//         end_date: "",
//       }));

//       // Reset attorney-related state
//       setAttorneyCount(0);
//       setAttorneyNumbers([]);
//       setAttorneyTMCounts({});
//       setAttorneyValidation({});
//       setAttorneyValidationMessages({});
//       setEditablePlanAmount(0);
//       setIsEditingAmount(false);

//       // Update order summary with customer details
//       if (selectedCustomer) {
//         setOrderSummary((prev) => ({
//           ...prev,
//           customerName: selectedCustomer.name || "-",
//           customerEmail: selectedCustomer.email || "-",
//           customerMobile: selectedCustomer.mobile || "-",
//           customerType: "-",
//           planName: "-",
//           planType: "-",
//           planAmount: 0,
//           totalTMCount: 0,
//           finalAmount: 0,
//           gstAmount: 0,
//           totalAmount: 0,
//           attorneyDetails: [],
//           end_date: "-",
//         }));
//       } else {
//         // Reset order summary if no customer selected
//         setOrderSummary({
//           customerName: "-",
//           customerEmail: "-",
//           customerMobile: "-",
//           customerType: "-",
//           planName: "-",
//           planType: "-",
//           planAmount: 0,
//           totalTMCount: 0,
//           finalAmount: 0,
//           gstAmount: 0,
//           totalAmount: 0,
//           includeGST: formData.gst_inclusion,
//           attorneyDetails: [],
//           end_date: "-",
//         });
//       }

//       setErrors((prev) => ({ ...prev, user_id: "" }));
//       return;
//     }

//     // ==================== PLAN SELECTION ====================
//     if (fieldName === "plan_id") {
//       // Validation checks
//       if (!formData.user_id) {
//         showError("user_id", "Please select Customer Name first.");
//         return;
//       }
//       if (!formData.is_attorney && formData.is_attorney !== "0") {
//         showError("is_attorney", "Please select Plan Type first.");
//         return;
//       }

//       const value = selectedOption?.value || null;

//       // Update form data
//       setFormData((prev) => ({
//         ...prev,
//         plan_id: value,
//         plan_type: null,
//         plan_price: 0,
//         total_amount: 0,
//         final_amount: 0,
//       }));

//       // Reset editable amount
//       setEditablePlanAmount(0);
//       setIsEditingAmount(false);

//       if (value) {
//         // Find selected plan
//         const selectedPlan = activePlans.find(
//           (plan) => plan.id === Number(value),
//         );

//         if (selectedPlan) {
//           // Handle attorney-specific logic
//           if (formData.is_attorney === "1") {
//             // For attorneys, get number of attorneys allowed
//             const numFeature = selectedPlan.features?.find(
//               (f) => f.feature_key === "number_of_attorney",
//             );
//             const num = numFeature ? Number(numFeature.default_value || 0) : 0;

//             console.log("Attorney-count from create-order", num)

//             setAttorneyCount(num);
//             setAttorneyNumbers(Array(num).fill(""));
//             setAttorneyTMCounts({});
//             setAttorneyValidation({});
//             setAttorneyValidationMessages({});
//           } else if (formData.is_attorney === "0") {
//             // For corporates, default to 1
//             setAttorneyCount(1);
//             setAttorneyNumbers([""]);
//             setAttorneyTMCounts({ "": 1 });
//             setAttorneyValidation({});
//             setAttorneyValidationMessages({});
//           }

//           // Update order summary
//           setOrderSummary((prev) => ({
//             ...prev,
//             planName: selectedPlan.name || "-",
//             planType: "-", // Reset plan type display
//             planAmount: 0, // Reset amounts
//             finalAmount: 0,
//             totalAmount: 0,
//             gstAmount: 0,
//           }));

//           toast.info("Please select Duration Type next.");
//         } else {
//           // Plan not found - reset attorney state
//           setAttorneyCount(0);
//           setAttorneyNumbers([]);
//           setAttorneyTMCounts({});
//           setAttorneyValidation({});
//           setAttorneyValidationMessages({});

//           setOrderSummary((prev) => ({
//             ...prev,
//             planName: "-",
//             planType: "-",
//           }));
//         }
//       } else {
//         // No plan selected - reset everything
//         setAttorneyCount(0);
//         setAttorneyNumbers([]);
//         setAttorneyTMCounts({});
//         setAttorneyValidation({});
//         setAttorneyValidationMessages({});

//         setOrderSummary((prev) => ({
//           ...prev,
//           planName: "-",
//           planType: "-",
//           planAmount: 0,
//           totalTMCount: 0,
//           finalAmount: 0,
//           gstAmount: 0,
//           totalAmount: 0,
//           attorneyDetails: [],
//         }));
//       }

//       setErrors((prev) => ({ ...prev, plan_id: "" }));
//       return;
//     }

//     // ==================== OTHER FIELDS ====================
//     // Handle any other select fields (payment_mode, bank_name, etc.)
//     const value = selectedOption?.value || "";

//     setFormData((prev) => ({
//       ...prev,
//       [fieldName]: value,
//     }));

//     setErrors((prev) => ({ ...prev, [fieldName]: "" }));
//   };

//   const handleGSTCheckboxChange = useCallback((e) => {
//     const checked = e.target.checked;

//     setFormData((prev) => ({
//       ...prev,
//       gst_inclusion: checked,
//     }));

//     if (checked) {
//       toast.info("GST (18%) will be added to the total amount");
//     } else {
//       toast.info("GST removed from total amount");
//     }
//   }, []);

//   const handleEditAmount = useCallback(() => {
//     setIsEditingAmount(true);
//     setEditablePlanAmount(safeNumber(formData.plan_price));
//   }, [formData.plan_price, safeNumber]);

//   const handleSaveAmount = useCallback(() => {
//     if (!editablePlanAmount || editablePlanAmount <= 0) {
//       toast.warning("Please enter a valid plan amount greater than 0");
//       return;
//     }

//     setIsEditingAmount(false);

//     setFormData((prev) => ({
//       ...prev,
//       plan_price: editablePlanAmount,
//     }));

//     setOrderSummary((prev) => ({
//       ...prev,
//       planAmount: Number(editablePlanAmount).toFixed(2),
//     }));

//     toast.success(
//       `Plan amount updated to ₹${Number(editablePlanAmount).toLocaleString("en-IN")}`,
//     );
//   }, [editablePlanAmount]);

//   // handleSaveTMCount:
//   const handleSaveTMCount = (count) => {
//     isTMCountManuallySet.current = true;
//     setOrderSummary((prev) => ({
//       ...prev,
//       totalTMCount: count,
//     }));
//     setFormData((prev) => ({
//       ...prev,
//       total_tm_count: count,
//     }));
//   };

//   // const handleSaveTMCount = (count) => {
//   //   setOrderSummary((prev) => ({
//   //     ...prev,
//   //     totalTMCount: count,
//   //   }));
//   //   setFormData((prev) => ({
//   //     ...prev,
//   //     total_tm_count: count,
//   //   }));
//   // };

//   // ==================== CUSTOMER MODAL FUNCTIONS ====================

//   const generatePassword = () => {
//     setIsGenerating(true);
//     const generatedPassword = cryptoRandomString({
//       length: 8,
//       type: "alphanumeric",
//     });
//     setPassword(generatedPassword);
//     setNewCustomer((prev) => ({
//       ...prev,
//       password: generatedPassword,
//     }));
//     setIsGenerating(false);
//   };

//   const handleNewCustomerChange = (e) => {
//     let { name, value } = e.target;

//     if (name === "mobile") {
//       value = value.replace(/\D/g, "").slice(0, 10);
//     }

//     setNewCustomer((prev) => ({ ...prev, [name]: value }));

//     const errorMsg = validateCustomerField(name, value);
//     setCustomerErrors((prev) => ({ ...prev, [name]: errorMsg }));
//   };

//   const validateCustomerField = (name, value) => {
//     let message = "";

//     if (name === "name") {
//       if (!value.trim()) {
//         message = "Name is required.";
//       } else if (!nameRegex.test(value)) {
//         message = "Enter a valid name (letters only).";
//       }
//     } else if (name === "email") {
//       if (!value.trim()) {
//         message = "Email is required.";
//       } else if (!emailRegex.test(value)) {
//         message = "Enter a valid email address.";
//       }
//     } else if (name === "mobile") {
//       if (!value.trim()) {
//         message = "Mobile number is required.";
//       } else if (!mobileRegex.test(value)) {
//         message = "Enter valid 10-digit mobile number.";
//       }
//     } else if (name === "address") {
//       if (!value.trim()) {
//         message = "Address is required.";
//       }
//     }

//     return message;
//   };

//   const handleAddCustomer = async (e) => {
//     e.preventDefault();

//     // if (!newCustomer.password) {
//     //   toast.warning("Password is required.");
//     //   return;
//     // }

//     if (!password) {
//       toast.warning("Password is required.");
//       return;
//     }

//     const customerData = {
//       name: newCustomer.name,
//       email: newCustomer.email,
//       mobile: newCustomer.mobile,
//       user_type: newCustomer.user_type || "customer",
//       password: password,
//       state:
//         statesData.find((s) => s.value === formData.state_name)?.label || "",
//       pincode: formData.pincode,
//       gst_inclusion: formData.gst_inclusion ? 1 : 0,
//       company_name: formData.gst_inclusion ? formData.company_name : "",
//       gstNumber: formData.gst_inclusion ? formData.gstNumber : "",
//       address: newCustomer.address,
//     };

//     addCustomerMutation.mutate(customerData);
//   };

//   // ==================== FORM SUBMISSION ====================

//   const validateForm = () => {
//     let errors = {};
//     const customerType = String(formData.customer_type);

//     if (!formData.user_id) errors.user_id = "select user name";
//     if (!formData.plan_id) errors.plan_id = "select plan";
//     if (formData.customer_type === "" || formData.customer_type === null) {
//       errors.customer_type = "Please select subscription type";
//     }

//     if (formData.is_attorney === "1" && (!attorneyNumbers || attorneyNumbers.length === 0 || attorneyNumbers.every(num => !num.trim()))) {
//       errors.attorney_details = "Please provide at least one attorney number";
//     }

//     if (formData.payment_status === "paid") {
//       if (!formData.payment_mode) {
//         errors.payment_mode = "Please select payment mode";
//       }

//       if (
//         (formData.payment_mode === "upi" ||
//           formData.payment_mode === "bank_transfer") &&
//         !formData.utr_no
//       ) {
//         errors.utr_no = "UTR Number is required";
//       }

//       if (formData.payment_mode === "bank_transfer" && !formData.bank_name) {
//         errors.bank_name = "Please select bank";
//       }

//       if (!formData.payment_attachment) {
//         errors.payment_attachment = "Attachment is required";
//       }
//     }

//     if (formData.is_attorney === "" || formData.is_attorney === null) {
//       errors.is_attorney = "Please select plan type";
//     }

//     if (customerType === "1") {
//       if (
//         !formData.plan_type ||
//         formData.plan_type === "" ||
//         formData.plan_type === "null"
//       ) {
//         errors.plan_type = "Please select plan duration";
//       }
//     }

//     if (formData.customer_type == 2 && !formData.end_date) {
//       errors.end_date = "Please select end date for free trial";
//     }
//     return errors;
//   };

//   const handleSubmit = async (event) => {
//     event.preventDefault();

//     const errors = validateForm();
//     if (Object.keys(errors).length > 0) {
//       const firstError = Object.values(errors)[0];
//       toast.warning(firstError);
//       setErrors(errors);
//       return;
//     }

//     const payload = new FormData();

//     payload.append("user_id", formData.user_id);
//     payload.append("plan_id", formData.plan_id);
//     payload.append("total_amount", Number(orderSummary.totalAmount));
//     payload.append("plan_price", Number(formData.plan_price));
//     payload.append("total_tm_count", orderSummary.totalTMCount);
//     payload.append(
//       "plan_type",
//       formData.customer_type === "2" ? "free_trial" : formData.plan_type,
//     );
//     payload.append("is_attorney", Number(formData.is_attorney));
//     // payload.append(
//     //   "attorney_number",
//     //   attorneyNumbers.filter((n) => n.trim()).join(","),
//     // );
//     payload.append(
//       "attorney_details",
//       JSON.stringify(
//         attorneyNumbers
//           .filter((n) => n.trim())
//           .map((num) => ({
//             attorney_number: num.trim(),
//             tm_count: attorneyTMCounts[num.trim()] ?? null,
//           })),
//       ),
//     );
//     payload.append("customer_type", Number(formData.customer_type));
//     payload.append(
//       "free_trial_up_to",
//       formData.customer_type === "2" ? formData.end_date : "",
//     );
//     payload.append("gst_inclusion", orderSummary.includeGST ? 1 : 0);
//     payload.append("gst_amount", Number(orderSummary.gstAmount));
//     payload.append("total_payable", Number(orderSummary.totalAmount));

//     if (formData.customer_type === "2" && formData.end_date) {
//       const days = dayjs(formData.end_date, "DD-MM-YYYY").diff(
//         dayjs().startOf("day"),
//         "day",
//       );
//       payload.append("free_trial_duration", days > 0 ? days : 0);
//     }

//     // PAYMENT LOGIC
//     if (formData.payment_status === "paid") {
//       payload.append("payment_status", 1);
//       payload.append("payment_mode", formData.payment_mode || "");
//       payload.append("bank_name", formData.bank_name || "");
//       payload.append("utr", formData.utr_no || "");
//       payload.append("amount_received", formData.amount_received || 0);

//       if (formData.payment_attachment) {
//         payload.append("payment_proof", formData.payment_attachment);
//       }
//     } else {
//       payload.append("payment_status", 0);
//     }

//     // log FormData
//     // for (let [key, value] of payload.entries()) {
//     //   console.log(`${key}:`, value);
//     // }

//     // ================= SUBMIT =================
//     setIsSubmitting(true);
//     createOrderMutation.mutate(payload, {
//       onSettled: () => {
//         setIsSubmitting(false);
//       },
//     });
//   };

//   const planOption = useMemo(() => {
//     if (!Array.isArray(activePlans)) return [];
//     return activePlans.map((item) => ({
//       value: item.id,
//       label: item.name,
//     }));
//   }, [activePlans]);

//   const planTypeOptions = [
//     { value: "1", label: "Attorney/IP Professional" },
//     { value: "0", label: "Corporates" },
//   ];

//   const customerTypeOptions = [
//     { value: "2", label: "Free Trial" },
//     { value: "1", label: "Paid" },
//   ];

//   const in_Menu_Options = [
//     { value: "monthly", label: "Monthly" },
//     { value: "annualy", label: "Annualy" },
//   ];

//   return (
//     <>
//       <div className="main-content">
//         <div className="page-content">
//           <div className="container-fluid">
//             {/* Breadcrumb */}
//             <div className="row">
//               <div className="col-12">
//                 <div className="page-title-box d-sm-flex align-items-center justify-content-between">
//                   <h4 className="mb-sm-0 font-size-18">
//                     {isEditing ? "Edit Subscription" : "Create Subscription"}
//                   </h4>
//                   <div className="page-title-right">
//                     <ol className="breadcrumb m-0">
//                       <li className="breadcrumb-item">
//                         <Link to="/">CMS</Link>
//                       </li>
//                       <li className="breadcrumb-item">
//                         <Link to="/cms/manage-customer/manage-order">
//                           Manage Subscription
//                         </Link>
//                       </li>
//                       <li className="breadcrumb-item active">
//                         {isEditing
//                           ? "Edit Subscription"
//                           : "Create Subscription"}
//                       </li>
//                     </ol>
//                   </div>
//                 </div>
//               </div>
//             </div>

//             <div className="row">
//               {/* Left Column - Form */}
//               <div className="col-lg-8 col-md-12">
//                 <Card className="shadow-sm">
//                   <div className="bg-light p-3 border-bottom">
//                     <Title level={4} className="mb-0">
//                       <FileTextOutlined className="me-2" />
//                       {isEditing ? "Edit Subscription" : "Create Subscription"}
//                     </Title>
//                   </div>
//                   <div className="card-body">
//                     <Spin spinning={isPageLoading}>
//                       <form ref={formRef} onSubmit={handleSubmit}>
//                         <Row gutter={[16, 16]}>
//                           <CustomerDetailsSection
//                             formData={formData}
//                             setFormData={setFormData}
//                             customerList={customersData}
//                             isLoading={customersLoading}
//                             errors={errors}
//                             setErrors={setErrors}
//                             calculateOrderSummary={calculateOrderSummary}
//                             setOrderSummary={setOrderSummary}
//                             customerTypeOptions={customerTypeOptions}
//                             planTypeOptions={planTypeOptions}
//                             handleCustomerTypeChange={handleCustomerTypeChange}
//                             handleCustomerPaymentTypeChange={
//                               handleCustomerPaymentTypeChange
//                             }
//                             setOpenAddCustomer={setOpenAddCustomer}
//                             isCustomerPreSelected={isCustomerPreSelected}
//                           />

//                           <PlanDetailsSection
//                             formData={formData}
//                             errors={errors}
//                             planOption={planOption}
//                             in_Menu_Options={in_Menu_Options}
//                             handleSelect={handleSelect}
//                             handlePlanTypeChange={handlePlanTypeChange}
//                             handleInputChange={handleInputChange}
//                             setOrderSummary={setOrderSummary}
//                           />

//                           <PaymentDetailsSection
//                             formData={formData}
//                             setFormData={setFormData}
//                             errors={errors}
//                             paymentModes={paymentModes}
//                             handleSelect={handleSelect}
//                             bankOptions={bankOptions}
//                             banksLoading={banksLoading}
//                           />

//                           <AttorneyNumbersSection
//                             attorneyCount={attorneyCount}
//                             attorneyNumbers={attorneyNumbers}
//                             attorneyValidation={attorneyValidation}
//                             attorneyValidationMessages={
//                               attorneyValidationMessages
//                             }
//                             attorneyTMCounts={attorneyTMCounts}
//                             isCheckingAttorney={isCheckingAttorney}
//                             handleAttorneyNumberChange={
//                               handleAttorneyNumberChange
//                             }
//                             handleCheckAttorneyStatus={handleAttorneyCheckClick}
//                             isAttorney={formData.is_attorney}
//                           />
//                         </Row>
//                       </form>
//                     </Spin>
//                   </div>
//                 </Card>
//               </div>

//               {/* Right Column - Order Summary */}
//               <div className="col-lg-4 col-md-12">
//                 <OrderSummary
//                   orderSummary={orderSummary}
//                   formData={formData}
//                   isEditing={isEditing}
//                   isEditingAmount={isEditingAmount}
//                   editablePlanAmount={editablePlanAmount}
//                   setEditablePlanAmount={setEditablePlanAmount}
//                   handleEditAmount={handleEditAmount}
//                   handleSaveAmount={handleSaveAmount}
//                   setIsEditingAmount={setIsEditingAmount}
//                   safeNumber={safeNumber}
//                   handleGSTCheckboxChange={handleGSTCheckboxChange}
//                   onSaveTMCount={handleSaveTMCount}
//                   isSubmitting={isSubmitting}
//                   formRef={formRef}
//                 />
//               </div>
//             </div>
//           </div>
//         </div>
//       </div>

//       {/* Add Customer Modal */}
//       <AddCustomerModal
//         open={openAddCustomer}
//         onCancel={() => setOpenAddCustomer(false)}
//         onSubmit={handleAddCustomer}
//         newCustomer={newCustomer}
//         setNewCustomer={setNewCustomer}
//         password={password}
//         setPassword={setPassword}
//         isGenerating={isGenerating}
//         generatePassword={generatePassword}
//         handleNewCustomerChange={handleNewCustomerChange}
//         states={statesData}
//         errors={errors}
//         customerErrors={customerErrors}
//         formData={formData}
//         setFormData={setFormData}
//         isAddingCustomer={addCustomerMutation.isPending}
//       />
//     </>
//   );
// };

// export default CreateOrder;

import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { Link, useParams, useNavigate, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import cryptoRandomString from "crypto-random-string";
import { Card, Spin, Row, Typography } from "antd";
import { FileTextOutlined } from "@ant-design/icons";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";

// Components
import CustomerDetailsSection from "./CustomerDetailsSection";
import PlanDetailsSection from "./PlanDetailsSection";
import PaymentDetailsSection from "./PaymentDetailsSection";
import AttorneyNumbersSection from "./AttorneyNumbersSection";
import OrderSummary from "./OrderSummary";
import AddCustomerModal from "../AddCustomerModal";

// Hooks and Services
import {
  useOrderForm,
  useOrderCalculations,
  useAttorneyValidation,
} from "../../hooks/useOrderForm";
import {
  fetchStates,
  loadCustomers,
  loadPlans,
  addCustomer,
  createOrder,
  fetchPlansByAttorneyType,
  fetchSingleOrderDetails,
  fetchAttorneyTMCounts,
  fetchPaymentModes,
  fetchBanks,
} from "../../services/orderService";

const { Title } = Typography;

const CreateOrder = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { order_id, user_id } = useParams();
  const isEditing = Boolean(order_id && user_id);
  const formRef = useRef(null);
  const isTMCountManuallySet = useRef(false);

  // Custom Hooks
  const {
    formData,
    setFormData,
    orderSummary,
    setOrderSummary,
    isEditingAmount,
    setIsEditingAmount,
    editablePlanAmount,
    setEditablePlanAmount,
    isSubmitting,
    setIsSubmitting,
    isCheckingAttorney,
    setIsCheckingAttorney,
    isGenerating,
    setIsGenerating,
    errors,
    setErrors,
    customerErrors,
    setCustomerErrors,
    attorneyCount,
    setAttorneyCount,
    attorneyNumbers,
    setAttorneyNumbers,
    attorneyValidation,
    setAttorneyValidation,
    attorneyValidationMessages,
    setAttorneyValidationMessages,
    attorneyTMCounts,
    setAttorneyTMCounts,
  } = useOrderForm();

  // ── Edit mode: attorney slot states ───────────────────────────────────────
  const [existingAttorneys, setExistingAttorneys] = useState([]); // [{ attorney_number, tm_count }]
  const [emptySlots, setEmptySlots] = useState([]); // [{ value, status, message, tmCount, isChecking }]
  const [editPlanLimit, setEditPlanLimit] = useState(0);
  const [attorneyNameOptions, setAttorneyNameOptions] = useState({}); // { [num]: [{attorney_name, tm_count}] }
  const [selectedAttorneyNames, setSelectedAttorneyNames] = useState({}); // { [num]: string[] }

  // Auto-select when only one attorney name is returned for a number
  useEffect(() => {
    Object.entries(attorneyNameOptions).forEach(([num, names]) => {
      if (names.length === 1) {
        setSelectedAttorneyNames((prev) => {
          if (prev[num]?.length > 0) return prev;
          return { ...prev, [num]: [names[0].attorney_name] };
        });
      }
    });
  }, [attorneyNameOptions]);

  // Local States
  const [newCustomer, setNewCustomer] = useState({
    name: "",
    email: "",
    mobile: "",
    // Customers are global now: the number alone is ambiguous.
    country_code: "+91",
    password: "",
    address: "",
  });
  const [password, setPassword] = useState("");
  const [openAddCustomer, setOpenAddCustomer] = useState(false);

  // Token
  const [token] = useState(localStorage.getItem("cms_token"));

  // Validation Patterns
  const nameRegex = /^[A-Za-z\s]{3,50}$/;
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  // 4-15 digits (E.164). The old [6-9] 10-digit rule rejected every foreign number.
  const mobileRegex = /^\d{4,15}$/;

  // ==================== TANSTACK QUERY - DATA FETCHING ====================

  // Fetch States
  const { data: statesData = [], isLoading: statesLoading } = useQuery({
    queryKey: ["states"],
    queryFn: fetchStates,
    staleTime: 5 * 60 * 1000,
  });

  // Fetch Customers
  const { data: customersData = [], isLoading: customersLoading } = useQuery({
    queryKey: ["customers"],
    queryFn: loadCustomers,
    staleTime: 2 * 60 * 1000,
    refetchOnMount: "always",
  });

  // Fetch Plans (Corporate)
  const { data: plansData = [], isLoading: plansLoading } = useQuery({
    queryKey: ["plans", formData.is_attorney],
    queryFn: () => fetchPlansByAttorneyType("0"),
    enabled: formData.is_attorney === "0",
    staleTime: 5 * 60 * 1000,
    refetchOnMount: true,
  });

  // Fetch Plans by Attorney Type
  const { data: attorneyPlansData = [], isLoading: attorneyPlansLoading } =
    useQuery({
      queryKey: ["attorneyPlans", formData.is_attorney],
      queryFn: () => fetchPlansByAttorneyType("1"),
      enabled: formData.is_attorney == "1",
      staleTime: 5 * 60 * 1000,
      refetchOnMount: true,
    });

  // Fetch Payment Modes
  // const { data: paymentModes = [] } = useQuery({
  //   queryKey: ["paymentModes"],
  //   queryFn: fetchPaymentModes,
  //   staleTime: 10 * 60 * 1000,
  // });

  // Fetch Banks
  const { data: bankData = [], isLoading: banksLoading } = useQuery({
    queryKey: ["bank-master"],
    queryFn: fetchBanks,
    staleTime: 1000 * 60 * 60,
  });

  const bankOptions = useMemo(() => {
    if (!Array.isArray(bankData)) return [];
    return bankData.map((bank) => ({
      value: bank.label,
      label: bank.label,
    }));
  }, [bankData]);

  // Fetch Single Order Details (Edit Mode)
  const {
    data: editOrderData,
    isLoading: isLoadingOrderDetails,
    refetch: refetchOrderDetails,
  } = useQuery({
    queryKey: ["orderDetails", order_id, user_id],
    queryFn: () => fetchSingleOrderDetails(order_id, user_id),
    enabled: isEditing,
    staleTime: 0,
    cacheTime: 0,
    refetchOnMount: "always",
  });

  // Combined loading state
  const isPageLoading = statesLoading || customersLoading || plansLoading;

  // ==================== TANSTACK QUERY - MUTATIONS ====================

  // Mutation: Add Customer
  const addCustomerMutation = useMutation({
    mutationFn: addCustomer,
    onSuccess: (response) => {
      if (response.status === 200 || response.success) {
        toast.success(response.message || "Customer added successfully!");

        queryClient.invalidateQueries({ queryKey: ["customers"] });

        const newCustomerId = response.data?.user_id;
        const newCustomerName = response.data?.name;
        const newCustomerEmail = response.data?.email;
        const newCustomerMobile = response.data?.mobile;

        setFormData((prev) => ({
          ...prev,
          user_id: newCustomerId,
        }));

        setOrderSummary((prev) => ({
          ...prev,
          customerName: newCustomerName || "-",
          customerEmail: newCustomerEmail || "-",
          customerMobile: newCustomerMobile || "-",
        }));

        setNewCustomer({
          name: "",
          email: "",
          mobile: "",
          password: "",
          address: "",
        });
        setPassword("");
        setCustomerErrors({});
        setOpenAddCustomer(false);
      } else {
        if (response.status === 422 && response.errors) {
          const formattedErrors = {};
          Object.entries(response.errors).forEach(([field, messages]) => {
            formattedErrors[field] = Array.isArray(messages)
              ? messages[0]
              : messages;
          });
          setCustomerErrors(formattedErrors);
          toast.warning("Please correct the highlighted fields.");
        } else {
          toast.error(response.message || "Failed to add customer.");
        }
      }
    },
    onError: (error) => {
      console.error("Error adding customer:", error);
      const serverErrors = error?.response?.data?.errors;
      if (serverErrors) {
        const formattedErrors = {};
        Object.entries(serverErrors).forEach(([field, messages]) => {
          formattedErrors[field] = Array.isArray(messages)
            ? messages[0]
            : messages;
        });
        setCustomerErrors(formattedErrors);
        toast.warning("Please correct the highlighted fields.");
      } else {
        toast.error(
          error?.response?.data?.message ||
            "Something went wrong while adding customer.",
        );
      }
    },
  });

  // Mutation: Create/Update Order
  const createOrderMutation = useMutation({
    mutationFn: (payload) => createOrder(payload, token),
    onSuccess: (response) => {
      if (response.status === 200 || response.success) {
        queryClient.invalidateQueries({ queryKey: ["orders"] });

        // Manual/offline payment → goes to pending approval queue
        if (response.pending_approval) {
          toast.success("Order created — sent for payment approval.");
          navigate("/cms/manage-customer/manage-order");
          return;
        }

        // Online payment link → show link to operator
        const paymentLink = response.payment_link || response.data?.payment_link;
        if (paymentLink && !isEditing) {
          toast.success("Payment link generated successfully!");
          navigate("/cms/manage-customer/manage-order", {
            state: { paymentLink, invoiceId: response.data?.invoice_id },
          });
          return;
        }

        toast.success(
          isEditing
            ? "Subscription updated successfully!"
            : "Subscription created successfully!",
        );
        navigate("/cms/manage-customer/manage-order");
      } else {
        toast.warning(
          response.message ||
            `Failed to ${isEditing ? "update" : "create"} subscription.`,
        );
      }
    },
    onError: (error) => {
      console.error("Error submitting subscription:", error);
      toast.error("Something went wrong while submitting the subscription.");
    },
  });

  // ==================== HELPER FUNCTIONS ====================

  const normalizeAttorneyTMResponse = (apiData) => {
    const tmCounts = {};
    const validation = {};
    const messages = {};

    apiData.details.forEach((item) => {
      const key = String(item.attorney_number);
      tmCounts[key] = Number(item.count || 0);
      validation[key] = "valid";
      messages[key] = `Valid - ${item.count} TMs`;
    });

    return { tmCounts, validation, messages };
  };

  // useEffect that auto-selects customer:
  useEffect(() => {
    if (!customersData.length) return;

    const params = new URLSearchParams(location.search);
    const customerId = params.get("customer_id");

    if (!customerId) return;

    const customer = customersData.find((c) => c.id === Number(customerId));
    if (!customer) return;

    setFormData((prev) => ({
      ...prev,
      user_id: Number(customerId),
    }));

    setOrderSummary((prev) => ({
      ...prev,
      customerName: customer.name || "-",
      customerEmail: customer.email || "-",
      customerMobile: customer.mobile || "-",
    }));
  }, [customersData, location.search]);

  const isCustomerPreSelected = Boolean(
    new URLSearchParams(location.search).get("customer_id"),
  );

  /* Set when this order is being raised for a converted lead. The backend uses
     it to close that exact lead (status 7 → Done); matching by email/phone
     would hit several leads that share a contact. */
  const originLeadId = new URLSearchParams(location.search).get("lead_id") || "";

  const activePlans = useMemo(() => {
    if (formData.is_attorney === "1") {
      const plans = Array.isArray(attorneyPlansData?.data)
        ? attorneyPlansData.data
        : [];
      return plans;
    }

    if (formData.is_attorney === "0") {
      const plans = Array.isArray(plansData?.data) ? plansData.data : [];
      return plans;
    }
    return [];
  }, [formData.is_attorney, attorneyPlansData, plansData]);

  // Calculations Hook
  const { calculateOrderSummary, safeNumber } = useOrderCalculations(
    formData,
    activePlans,
    customersData,
    isEditingAmount,
    editablePlanAmount,
    attorneyNumbers,
    attorneyTMCounts,
    attorneyValidation,
  );

  // Attorney Validation Hook — same validation logic for both create & edit
  const { handleCheckAttorneyStatus } = useAttorneyValidation(
    setIsCheckingAttorney,
    setAttorneyValidation,
    setAttorneyValidationMessages,
    setAttorneyTMCounts,
    calculateOrderSummary,
    setAttorneyNameOptions,
  );

  // ==================== UTILITY FUNCTIONS ====================

  const showError = (field, message) => {
    toast.warning(message);
    setErrors((prev) => ({ ...prev, [field]: message }));
  };

  const formatPrice = (price) =>
    Number(price || 0).toLocaleString("en-IN", {
      style: "currency",
      currency: "INR",
    });

  // ==================== UNIFIED ATTORNEY CHECK HANDLER ====================
  // Create mode: validates all attorney numbers
  // Edit mode: validates only new empty slot attorneys (existing are locked)
  const handleAttorneyCheckClick = () => {
    isTMCountManuallySet.current = false;

    if (isEditing) {
      // Edit mode: validate existing attorneys + filled empty slots
      const existingNums = existingAttorneys.map((a) => String(a.attorney_number));
      const newNums = emptySlots
        .map((s) => s.value.trim())
        .filter((v) => v !== "");
      const allNums = [...existingNums, ...newNums];

      if (allNums.length === 0) {
        toast.warning("Please enter at least one new attorney number.");
        return;
      }
      handleCheckAttorneyStatus(allNums);

      // After validation completes, update emptySlots state from the
      // validation/tmCounts that useAttorneyValidation sets
      // (handled in the useEffect below)
    } else {
      // Create mode: validate all
      handleCheckAttorneyStatus(attorneyNumbers);
    }
  };

  // ── Edit mode: sync empty slots after validation completes ──────────────
  // When handleCheckAttorneyStatus finishes, it sets attorneyValidation/TM/Messages
  // We need to map those back into emptySlots for the UI
  useEffect(() => {
    if (!isEditing || emptySlots.length === 0) return;

    setEmptySlots((prev) =>
      prev.map((slot) => {
        const num = slot.value.trim();
        if (!num) return slot;

        const validation = attorneyValidation[num];
        const tmCount = attorneyTMCounts[num] || 0;
        const message = attorneyValidationMessages[num] || "";

        if (validation === "valid") {
          return {
            ...slot,
            status: "valid",
            message,
            tmCount,
            isChecking: false,
          };
        } else if (validation === "invalid") {
          return {
            ...slot,
            status: "invalid",
            message,
            tmCount: 0,
            isChecking: false,
          };
        }
        return slot;
      }),
    );
  }, [
    isEditing,
    attorneyValidation,
    attorneyTMCounts,
    attorneyValidationMessages,
  ]);

  // ── Edit mode: slot input change handler ────────────────────────────────
  const handleSlotInputChange = (index, value) => {
    const sanitized = value.replace(/\D/g, "");

    setEmptySlots((prev) =>
      prev.map((s, i) =>
        i === index
          ? { ...s, value: sanitized, status: null, message: "", tmCount: 0 }
          : s,
      ),
    );
  };

  // ── TM count manual override ─────────────────────────────────────────────
  const handleTMCountChange = (num, newCount) => {
    // Always update the central map (drives order summary calculations)
    setAttorneyTMCounts((prev) => ({ ...prev, [String(num)]: newCount }));
    // Keep existingAttorneys in sync (drives stats bar in edit mode)
    setExistingAttorneys((prev) =>
      prev.map((a) =>
        String(a.attorney_number) === String(num) ? { ...a, tm_count: newCount } : a,
      ),
    );
    // Keep emptySlots in sync (drives stats bar in edit mode)
    setEmptySlots((prev) =>
      prev.map((s) =>
        s.value.trim() === String(num) ? { ...s, tmCount: newCount } : s,
      ),
    );
  };

  // ── Edit mode: per-row slot check ───────────────────────────────────────
  const handleCheckSlotStatus = async (index) => {
    const num = emptySlots[index]?.value.trim();
    if (!num) {
      toast.warning("Please enter an attorney number first.");
      return;
    }

    // Use the same validation hook for a single number
    handleCheckAttorneyStatus([num]);
  };

  // ==================== EDIT MODE - POPULATE FORM DATA ====================

  useEffect(() => {
    if (!isEditing || !editOrderData?.data) return;

    const data = editOrderData.data;
    const isAttorney = String(data.is_attorney);

    const populateEditData = () => {
      const attorneys = (
        editOrderData.attorney_numbers ||
        editOrderData.attorney_number ||
        []
      ).map((item) =>
        typeof item === "object" ? String(item.attorney_number) : String(item),
      );
      const customerType = String(data.customer_type);
      const gstInclusion = data.gst_inclusion === 1;

      let planType = null;
      if (customerType === "2") {
        planType = null;
      } else if ((data.latest_order_plan_type || data.plan_type) && (data.latest_order_plan_type || data.plan_type) !== "free_trial") {
        planType = data.latest_order_plan_type || data.plan_type;
      }

      // Populate form data
      setFormData((prev) => ({
        ...prev,
        user_id: data.user_id,
        plan_id: Number(data.plan_id),
        customer_type: customerType,
        plan_type: planType,
        is_attorney: isAttorney,
        end_date: data.subscription_end_date || "",
        gst_inclusion: gstInclusion,
        plan_price: Number(data.plan_price || 0),
        total_tm_count: Number(data.total_tm_count || 0),
        payment_status: (data.last_payment_status || data.payment_status) === "1" ? "manual" : "link",
        payment_mode: data.last_payment_mode || data.payment_mode || "",
        amount_received: data.amount_received || "",
        bank_name: data.bank_name || "",
        utr_no: data.utr_no || "",
        payment_attachment: null,
      }));

      // Calculate duration for edit mode (free trial)
      if (customerType === "2" && data.subscription_end_date) {
        const endDate = dayjs(data.subscription_end_date, "DD-MM-YYYY");
        const today = dayjs().startOf("day");
        const days = endDate.diff(today, "day");
        setOrderSummary((prev) => ({
          ...prev,
          trialDuration: days > 0 ? days : 0,
        }));
      }

      // Handle attorney numbers — NO auto-check, just display locked + empty slots
      if (
        Array.isArray(attorneys) &&
        attorneys.length > 0 &&
        isAttorney === "1"
      ) {
        const attorneyDetails = editOrderData.attorney_numbers || [];

        // Build lookup maps: attorney_number -> tm_count / attorney_names
        const tmLookup = {};
        const namesLookup = {};
        const preSelected = {};
        attorneyDetails.forEach((item) => {
          const key = String(item.attorney_number);
          tmLookup[key] = Number(item.tm_count || 0);
          if (item.attorney_names?.length) {
            namesLookup[key] = item.attorney_names.map((n) =>
              typeof n === "string" ? { attorney_name: n } : n,
            );
            preSelected[key] = item.attorney_names.map((n) =>
              typeof n === "string" ? n : n.attorney_name,
            );
          }
        });

        // Set existing attorneys as locked with their actual TM counts
        const lockedAttorneys = attorneys.map((num) => ({
          attorney_number: String(num),
          tm_count: tmLookup[String(num)] || 0,
        }));
        setExistingAttorneys(lockedAttorneys);
        setAttorneyNameOptions(namesLookup);
        setSelectedAttorneyNames(preSelected);

        // Also set in the old state for calculation compatibility
        setAttorneyNumbers(attorneys);
        setAttorneyCount(attorneys.length);

        // Pre-populate TM counts for order summary calculations
        const tmCounts = {};
        const validation = {};
        const messages = {};
        attorneys.forEach((num) => {
          const count = tmLookup[String(num)] || 0;
          tmCounts[String(num)] = count;
          validation[String(num)] = "valid";
          messages[String(num)] = `Existing — ${count} TMs`;
        });
        setAttorneyTMCounts(tmCounts);
        setAttorneyValidation(validation);
        setAttorneyValidationMessages(messages);

        // Empty slots will be set once plans load (see useEffect below)
        setEmptySlots([]);
        setEditPlanLimit(0);
      } else if (isAttorney === "0") {
        setExistingAttorneys([]);
        setEmptySlots([]);
        setEditPlanLimit(0);
        setAttorneyCount(1);
        setAttorneyNumbers([""]);
        setAttorneyTMCounts({});

        /* An existing subscription was sold at a particular count — possibly one
           the rep set by hand — so the saved figure wins over the plan's
           included count. Only a record with none falls back to the plan. */
        const savedTMCount = Number(data.total_tm_count || 0);
        if (savedTMCount > 0) {
          isTMCountManuallySet.current = true;
          setOrderSummary((prev) => ({ ...prev, totalTMCount: savedTMCount }));
        }
      }

      setEditablePlanAmount(Number(data.plan_price));
      setIsEditingAmount(false);
    };

    populateEditData();
  }, [isEditing, editOrderData?.data, attorneyPlansLoading, plansLoading]);

  // ── Edit mode: calculate empty slots once plans load ────────────────────
  useEffect(() => {
    if (!isEditing || !editOrderData?.data) return;
    if (existingAttorneys.length === 0) return;

    const isAttorney = String(editOrderData.data.is_attorney);
    if (isAttorney !== "1") return;

    const planId = Number(editOrderData.data.plan_id);
    if (!planId || activePlans.length === 0) return;

    const selectedPlan = activePlans.find((p) => p.id === planId);
    if (!selectedPlan) return;

    const numFeature = selectedPlan.features?.find(
      (f) => f.feature_key === "number_of_attorney",
    );
    const limit = numFeature ? Number(numFeature.default_value || 0) : 0;
    setEditPlanLimit(limit);

    const emptyCount = Math.max(0, limit - existingAttorneys.length);
    setEmptySlots(
      Array.from({ length: emptyCount }, () => ({
        value: "",
        status: null,
        message: "",
        tmCount: 0,
        isChecking: false,
      })),
    );

    // Update attorneyCount to reflect total (existing + empty)
    setAttorneyCount(limit);
  }, [isEditing, editOrderData?.data, activePlans, existingAttorneys.length]);

  // ==================== CALCULATIONS ====================

  useEffect(() => {
    if (customersData.length === 0) return;

    if (formData.customer_type == 2) {
      const summary = calculateOrderSummary();

      const finalTMCount = isTMCountManuallySet.current
        ? orderSummary.totalTMCount
        : summary.totalTMCount;

      let trialDuration = null;
      if (formData.end_date) {
        const days = dayjs(formData.end_date, "DD-MM-YYYY").diff(
          dayjs().startOf("day"),
          "day",
        );
        trialDuration = days > 0 ? days : 0;
      }

      setOrderSummary((prev) => ({
        ...summary,
        trialDuration: trialDuration ?? prev.trialDuration,
        totalTMCount: finalTMCount,
      }));
      setFormData((prev) => ({
        ...prev,
        total_amount: 0,
        final_amount: 0,
        total_tm_count: finalTMCount,
      }));
      return;
    }

    // Wait for plans to load
    if (
      formData.is_attorney === "1" &&
      (!attorneyPlansData || attorneyPlansData.length === 0)
    ) {
      return;
    }

    if (
      formData.is_attorney === "0" &&
      (!plansData || plansData.length === 0)
    ) {
      return;
    }

    if (formData.customer_type == 1) {
      if (!formData.plan_id || !formData.plan_type) {
        setOrderSummary((prev) => ({
          ...prev,
          planAmount: 0,
          finalAmount: 0,
          totalAmount: 0,
          /* Nothing is included until a plan is picked — the corporate figure
             is the plan's, so showing 1 here would be inventing one. */
          totalTMCount: 0,
          gstAmount: 0,
        }));

        setFormData((prev) => ({
          ...prev,
          total_amount: 0,
          final_amount: 0,
          total_tm_count: 0,
        }));

        return;
      }
    }

    const summary = calculateOrderSummary();

    const finalTMCount = isTMCountManuallySet.current
      ? orderSummary.totalTMCount
      : summary.totalTMCount;

    const finalPlanAmount = isEditingAmount
      ? editablePlanAmount
      : Number(summary.planAmount);

    const finalAmount = finalPlanAmount * finalTMCount;
    const gstAmount = summary.includeGST ? finalAmount * 0.18 : 0;
    const totalAmount = finalAmount + gstAmount;

    setOrderSummary(
      isTMCountManuallySet.current
        ? {
            ...summary,
            totalTMCount: finalTMCount,
            planAmount: Number(finalPlanAmount).toFixed(2),
            finalAmount: Number(finalAmount).toFixed(2),
            gstAmount: Number(gstAmount).toFixed(2),
            totalAmount: Number(totalAmount).toFixed(2),
          }
        : summary,
    );

    setFormData((prev) => ({
      ...prev,
      total_amount: isTMCountManuallySet.current
        ? finalPlanAmount
        : summary.planAmount,
      final_amount: isTMCountManuallySet.current
        ? totalAmount
        : summary.totalAmount,
      total_tm_count: finalTMCount,
    }));
  }, [
    customersData,
    plansData,
    attorneyPlansData,
    formData.plan_id,
    formData.plan_type,
    formData.customer_type,
    formData.user_id,
    formData.end_date,
    formData.gst_inclusion,
    formData.plan_price,
    formData.is_attorney,
    editablePlanAmount,
    isEditingAmount,
    attorneyTMCounts,
    attorneyValidation,
  ]);

  // ==================== ATTORNEY HANDLING ====================

  const handleAttorneyNumberChange = (index, value) => {
    const sanitized = value.replace(/\D/g, "");
    const oldNumber = attorneyNumbers[index]; // capture before update

    setAttorneyNumbers((prev) => {
      const updated = [...prev];

      if (!sanitized) {
        updated[index] = "";
        // Remove stale TM count for the old number when field is cleared
        if (oldNumber) {
          setAttorneyTMCounts((prevCounts) => {
            const copy = { ...prevCounts };
            delete copy[oldNumber];
            return copy;
          });
        }
        return updated;
      }

      const isDuplicate = prev.some(
        (num, i) => i !== index && num === sanitized,
      );

      if (isDuplicate) {
        toast.warning("Attorney number must be unique");

        setAttorneyValidation((prevVal) => ({
          ...prevVal,
          [sanitized]: "invalid",
        }));

        setAttorneyValidationMessages((prevMsg) => ({
          ...prevMsg,
          [sanitized]: "Duplicate attorney number",
        }));

        return prev;
      }

      updated[index] = sanitized;

      // Remove stale TM count for the old number when attorney number changes
      if (oldNumber && oldNumber !== sanitized) {
        setAttorneyTMCounts((prevCounts) => {
          const copy = { ...prevCounts };
          delete copy[oldNumber];
          return copy;
        });
      }

      setAttorneyValidation((prevVal) => {
        const copy = { ...prevVal };
        delete copy[sanitized];
        if (oldNumber && oldNumber !== sanitized) delete copy[oldNumber];
        return copy;
      });

      setAttorneyValidationMessages((prevMsg) => {
        const copy = { ...prevMsg };
        delete copy[sanitized];
        if (oldNumber && oldNumber !== sanitized) delete copy[oldNumber];
        return copy;
      });

      return updated;
    });
  };

  // ==================== FORM HANDLERS ====================

  const handleInputChange = useCallback((event, fieldName = null) => {
    let name, value;

    if (fieldName) {
      name = fieldName;
      value = event?.value || "";
    } else {
      name = event.target.name;
      value = event.target.value;
    }

    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));

    setErrors((prev) => ({ ...prev, [name]: "" }));
  }, []);

  const handleCustomerTypeChange = (option) => {
    if (!formData.user_id) {
      showError("user_id", "Please select Customer Name first.");
      return;
    }

    const selectedType = option ? option.value : "";

    // COMPLETE RESET
    setFormData((prev) => ({
      ...prev,
      is_attorney: selectedType,
      plan_id: null,
      plan_type: null,
      plan_price: 0,
      total_amount: 0,
      final_amount: 0,
      total_tm_count: 0,
      end_date: "",
    }));

    // RESET ATTORNEY STATE
    if (selectedType === "0") {
      setAttorneyCount(1);
      setAttorneyNumbers([""]);
      /* The count arrives with the plan (tm_document_size), not from here. */
      setAttorneyTMCounts({});
      setAttorneyValidation({});
      setAttorneyValidationMessages({});
    } else {
      setAttorneyCount(0);
      setAttorneyNumbers([]);
      setAttorneyTMCounts({});
      setAttorneyValidation({});
      setAttorneyValidationMessages({});
    }

    setEditablePlanAmount(0);
    setIsEditingAmount(false);

    setOrderSummary((prev) => ({
      ...prev,
      customerName: prev.customerName,
      customerEmail: prev.customerEmail,
      customerMobile: prev.customerMobile,
      customerType: prev.customerType,
      planName: "-",
      planType: "-",
      planAmount: 0,
      totalTMCount: 0,
      finalAmount: 0,
      gstAmount: 0,
      totalAmount: 0,
      includeGST: prev.includeGST,
      attorneyDetails: [],
      end_date: "",
    }));

    setErrors((prev) => ({ ...prev, is_attorney: "" }));
  };

  // Handler for Paid/Free Trial selection
  const handleCustomerPaymentTypeChange = (option) => {
    if (!formData.user_id) {
      showError("user_id", "Please select Customer Name first.");
      return;
    }

    const selectedPaymentType = option ? option.value : "";

    /* Paid and trial include different numbers of trademarks, so a count typed
       under one should not follow the rep into the other. */
    isTMCountManuallySet.current = false;

    setFormData((prev) => ({
      ...prev,
      customer_type: selectedPaymentType,
      end_date: selectedPaymentType === "2" ? prev.end_date : "",
    }));

    setOrderSummary((prev) => ({
      ...prev,
      customerType:
        selectedPaymentType === "1"
          ? "Paid"
          : selectedPaymentType === "2"
            ? "Free Trial"
            : "-",
      end_date: selectedPaymentType === "2" ? prev.end_date : "-",
    }));

    setErrors((prev) => ({ ...prev, customer_type: "" }));
  };

  const handlePlanTypeChange = useCallback(
    (selectedOption) => {
      if (!formData.user_id) {
        showError("user_id", "Please select Customer Name first.");
        return;
      }
      if (!formData.is_attorney && formData.is_attorney !== "0") {
        showError("is_attorney", "Please select Plan Type first.");
        return;
      }
      if (!formData.plan_id) {
        showError("plan_id", "Please select Plan Name first.");
        return;
      }

      const selectedType = selectedOption?.value || "";

      const selectedPlan = activePlans.find(
        (plan) => plan.id === Number(formData.plan_id),
      );

      if (selectedPlan) {
        const amount =
          selectedType === "monthly"
            ? Number(selectedPlan.monthly_price)
            : Number(selectedPlan.annual_price);

        const defaultEndDate =
          selectedType === "monthly"
            ? dayjs().add(1, "month").format("DD/MM/YYYY")
            : dayjs().add(1, "year").format("DD/MM/YYYY");

        setFormData((prev) => ({
          ...prev,
          plan_type: selectedType,
          plan_price: amount,
          end_date: defaultEndDate,
        }));

        setEditablePlanAmount(amount);
        setIsEditingAmount(false);

        setOrderSummary((prev) => ({
          ...prev,
          planType: selectedType === "monthly" ? "Monthly" : "Annual",
          planAmount: amount.toFixed(2),
          end_date: defaultEndDate,
        }));

        setErrors((prev) => ({ ...prev, plan_type: "" }));

        toast.success(
          `Plan amount updated to ₹${amount.toLocaleString("en-IN")}`,
        );
      }
    },
    [formData.user_id, formData.is_attorney, formData.plan_id, activePlans],
  );

  // const handleSelect = (fieldName, selectedOption) => {
  //   // ==================== CUSTOMER SELECTION ====================
  //   if (fieldName === "user_id") {
  //     const value = selectedOption?.value || null;
  //     const selectedCustomer = customersData.find(
  //       (c) => c.id === Number(value),
  //     );

  //     setFormData((prev) => ({
  //       ...prev,
  //       user_id: value,
  //       is_attorney: null,
  //       plan_id: null,
  //       plan_type: null,
  //       plan_price: 0,
  //       total_amount: 0,
  //       final_amount: 0,
  //       total_tm_count: 0,
  //       end_date: "",
  //     }));

  //     setAttorneyCount(0);
  //     setAttorneyNumbers([]);
  //     setAttorneyTMCounts({});
  //     setAttorneyValidation({});
  //     setAttorneyValidationMessages({});
  //     setEditablePlanAmount(0);
  //     setIsEditingAmount(false);

  //     if (selectedCustomer) {
  //       setOrderSummary((prev) => ({
  //         ...prev,
  //         customerName: selectedCustomer.name || "-",
  //         customerEmail: selectedCustomer.email || "-",
  //         customerMobile: selectedCustomer.mobile || "-",
  //         customerType: "-",
  //         planName: "-",
  //         planType: "-",
  //         planAmount: 0,
  //         totalTMCount: 0,
  //         finalAmount: 0,
  //         gstAmount: 0,
  //         totalAmount: 0,
  //         attorneyDetails: [],
  //         end_date: "-",
  //       }));
  //     } else {
  //       setOrderSummary({
  //         customerName: "-",
  //         customerEmail: "-",
  //         customerMobile: "-",
  //         customerType: "-",
  //         planName: "-",
  //         planType: "-",
  //         planAmount: 0,
  //         totalTMCount: 0,
  //         finalAmount: 0,
  //         gstAmount: 0,
  //         totalAmount: 0,
  //         includeGST: formData.gst_inclusion,
  //         attorneyDetails: [],
  //         end_date: "-",
  //       });
  //     }

  //     setErrors((prev) => ({ ...prev, user_id: "" }));
  //     return;
  //   }

  //   // ==================== PLAN SELECTION ====================
  //   if (fieldName === "plan_id") {
  //     if (!formData.user_id) {
  //       showError("user_id", "Please select Customer Name first.");
  //       return;
  //     }
  //     if (!formData.is_attorney && formData.is_attorney !== "0") {
  //       showError("is_attorney", "Please select Plan Type first.");
  //       return;
  //     }

  //     const value = selectedOption?.value || null;

  //     setFormData((prev) => ({
  //       ...prev,
  //       plan_id: value,
  //       plan_type: null,
  //       plan_price: 0,
  //       total_amount: 0,
  //       final_amount: 0,
  //     }));

  //     setEditablePlanAmount(0);
  //     setIsEditingAmount(false);

  //     if (value) {
  //       const selectedPlan = activePlans.find(
  //         (plan) => plan.id === Number(value),
  //       );

  //       if (selectedPlan) {
  //         if (formData.is_attorney === "1") {
  //           const numFeature = selectedPlan.features?.find(
  //             (f) => f.feature_key === "number_of_attorney",
  //           );
  //           const num = numFeature ? Number(numFeature.default_value || 0) : 0;

  //           console.log("Attorney-count from create-order", num);

  //           setAttorneyCount(num);
  //           setAttorneyNumbers(Array(num).fill(""));
  //           setAttorneyTMCounts({});
  //           setAttorneyValidation({});
  //           setAttorneyValidationMessages({});
  //         } else if (formData.is_attorney === "0") {
  //           setAttorneyCount(1);
  //           setAttorneyNumbers([""]);
  //           setAttorneyTMCounts({ "": 1 });
  //           setAttorneyValidation({});
  //           setAttorneyValidationMessages({});
  //         }

  //         setOrderSummary((prev) => ({
  //           ...prev,
  //           planName: selectedPlan.name || "-",
  //           planType: "-",
  //           planAmount: 0,
  //           finalAmount: 0,
  //           totalAmount: 0,
  //           gstAmount: 0,
  //         }));

  //         toast.info("Please select Duration Type next.");
  //       } else {
  //         setAttorneyCount(0);
  //         setAttorneyNumbers([]);
  //         setAttorneyTMCounts({});
  //         setAttorneyValidation({});
  //         setAttorneyValidationMessages({});

  //         setOrderSummary((prev) => ({
  //           ...prev,
  //           planName: "-",
  //           planType: "-",
  //         }));
  //       }
  //     } else {
  //       setAttorneyCount(0);
  //       setAttorneyNumbers([]);
  //       setAttorneyTMCounts({});
  //       setAttorneyValidation({});
  //       setAttorneyValidationMessages({});

  //       setOrderSummary((prev) => ({
  //         ...prev,
  //         planName: "-",
  //         planType: "-",
  //         planAmount: 0,
  //         totalTMCount: 0,
  //         finalAmount: 0,
  //         gstAmount: 0,
  //         totalAmount: 0,
  //         attorneyDetails: [],
  //       }));
  //     }

  //     setErrors((prev) => ({ ...prev, plan_id: "" }));
  //     return;
  //   }

  //   // ==================== OTHER FIELDS ====================
  //   const value = selectedOption?.value || "";

  //   setFormData((prev) => ({
  //     ...prev,
  //     [fieldName]: value,
  //   }));

  //   setErrors((prev) => ({ ...prev, [fieldName]: "" }));
  // };


  const handleSelect = (fieldName, selectedOption) => {
    // ==================== CUSTOMER SELECTION ====================
    if (fieldName === "user_id") {
      const value = selectedOption?.value || null;
      const selectedCustomer = customersData.find(
        (c) => c.id === Number(value),
      );

      setFormData((prev) => ({
        ...prev,
        user_id: value,
        is_attorney: null,
        plan_id: null,
        plan_type: null,
        plan_price: 0,
        total_amount: 0,
        final_amount: 0,
        total_tm_count: 0,
        end_date: "",
      }));

      setAttorneyCount(0);
      setAttorneyNumbers([]);
      setAttorneyTMCounts({});
      setAttorneyValidation({});
      setAttorneyValidationMessages({});
      setEditablePlanAmount(0);
      setIsEditingAmount(false);

      if (selectedCustomer) {
        setOrderSummary((prev) => ({
          ...prev,
          customerName: selectedCustomer.name || "-",
          customerEmail: selectedCustomer.email || "-",
          customerMobile: selectedCustomer.mobile || "-",
          customerType: "-",
          planName: "-",
          planType: "-",
          planAmount: 0,
          totalTMCount: 0,
          finalAmount: 0,
          gstAmount: 0,
          totalAmount: 0,
          attorneyDetails: [],
          end_date: "-",
        }));
      } else {
        setOrderSummary({
          customerName: "-",
          customerEmail: "-",
          customerMobile: "-",
          customerType: "-",
          planName: "-",
          planType: "-",
          planAmount: 0,
          totalTMCount: 0,
          finalAmount: 0,
          gstAmount: 0,
          totalAmount: 0,
          includeGST: formData.gst_inclusion,
          attorneyDetails: [],
          end_date: "-",
        });
      }

      setErrors((prev) => ({ ...prev, user_id: "" }));
      return;
    }

    // ==================== PLAN SELECTION ====================
    if (fieldName === "plan_id") {
      if (!formData.user_id) {
        showError("user_id", "Please select Customer Name first.");
        return;
      }
      if (!formData.is_attorney && formData.is_attorney !== "0") {
        showError("is_attorney", "Please select Plan Type first.");
        return;
      }

      const value = selectedOption?.value || null;

      setFormData((prev) => ({
        ...prev,
        plan_id: value,
        plan_type: null,
        plan_price: 0,
        total_amount: 0,
        final_amount: 0,
      }));

      setEditablePlanAmount(0);
      setIsEditingAmount(false);

      if (value) {
        const selectedPlan = activePlans.find(
          (plan) => plan.id === Number(value),
        );

        if (selectedPlan) {
          if (formData.is_attorney === "1") {
            const numFeature = selectedPlan.features?.find(
              (f) => f.feature_key === "number_of_attorney",
            );
            const num = numFeature ? Number(numFeature.default_value || 0) : 0;

            // console.log("Attorney-count from create-order", num);

            setAttorneyCount(num);

            if (isEditing) {
              // Edit mode: keep existing locked attorneys, only recalculate empty slots
              setEditPlanLimit(num);
              const newEmptyCount = Math.max(0, num - existingAttorneys.length);
              setEmptySlots(
                Array.from({ length: newEmptyCount }, () => ({
                  value: "",
                  status: null,
                  message: "",
                  tmCount: 0,
                  isChecking: false,
                })),
              );
              // Clear only the new-slot validation state (existing attorneys stay locked)
              setAttorneyValidation({});
              setAttorneyValidationMessages({});
            } else {
              // Create mode: full reset (unchanged)
              setAttorneyNumbers(Array(num).fill(""));
              setAttorneyTMCounts({});
              setAttorneyValidation({});
              setAttorneyValidationMessages({});
              setExistingAttorneys([]);
              setEditPlanLimit(num);
              setEmptySlots(
                Array.from({ length: num }, () => ({
                  value: "",
                  status: null,
                  message: "",
                  tmCount: 0,
                  isChecking: false,
                })),
              );
            }
          } else if (formData.is_attorney === "0") {
            setAttorneyCount(1);
            setAttorneyNumbers([""]);
            /* No attorney numbers to total up — the corporate TM count comes
               from the plan's tm_document_size, so leave this empty rather than
               seeding a placeholder the summary would then add in. */
            setAttorneyTMCounts({});
            setAttorneyValidation({});
            setAttorneyValidationMessages({});
            /* A new plan means a new included count. Anything the rep typed was
               for the old plan, so let the new one's figure through. */
            isTMCountManuallySet.current = false;
          }

          setOrderSummary((prev) => ({
            ...prev,
            planName: selectedPlan.name || "-",
            planType: "-",
            planAmount: 0,
            finalAmount: 0,
            totalAmount: 0,
            gstAmount: 0,
          }));

          toast.info("Please select Duration Type next.");
        } else {
          setAttorneyCount(0);
          setAttorneyNumbers([]);
          setAttorneyTMCounts({});
          setAttorneyValidation({});
          setAttorneyValidationMessages({});
          setExistingAttorneys([]);
          setEmptySlots([]);
          setEditPlanLimit(0);

          setOrderSummary((prev) => ({
            ...prev,
            planName: "-",
            planType: "-",
          }));
        }
      } else {
        setAttorneyCount(0);
        setAttorneyNumbers([]);
        setAttorneyTMCounts({});
        setAttorneyValidation({});
        setAttorneyValidationMessages({});
        setExistingAttorneys([]);
        setEmptySlots([]);
        setEditPlanLimit(0);

        setOrderSummary((prev) => ({
          ...prev,
          planName: "-",
          planType: "-",
          planAmount: 0,
          totalTMCount: 0,
          finalAmount: 0,
          gstAmount: 0,
          totalAmount: 0,
          attorneyDetails: [],
        }));
      }

      setErrors((prev) => ({ ...prev, plan_id: "" }));
      return;
    }

    // ==================== OTHER FIELDS ====================
    const value = selectedOption?.value || "";

    setFormData((prev) => ({
      ...prev,
      [fieldName]: value,
    }));

    setErrors((prev) => ({ ...prev, [fieldName]: "" }));
  };


  const handleGSTCheckboxChange = useCallback((e) => {
    const checked = e.target.checked;

    setFormData((prev) => ({
      ...prev,
      gst_inclusion: checked,
    }));

    if (checked) {
      toast.info("GST (18%) will be added to the total amount");
    } else {
      toast.info("GST removed from total amount");
    }
  }, []);

  const handleEditAmount = useCallback(() => {
    setIsEditingAmount(true);
    setEditablePlanAmount(safeNumber(formData.plan_price));
  }, [formData.plan_price, safeNumber]);

  const handleSaveAmount = useCallback(() => {
    if (!editablePlanAmount || editablePlanAmount <= 0) {
      toast.warning("Please enter a valid plan amount greater than 0");
      return;
    }

    setIsEditingAmount(false);

    setFormData((prev) => ({
      ...prev,
      plan_price: editablePlanAmount,
    }));

    setOrderSummary((prev) => ({
      ...prev,
      planAmount: Number(editablePlanAmount).toFixed(2),
    }));

    toast.success(
      `Plan amount updated to ₹${Number(editablePlanAmount).toLocaleString("en-IN", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`,
    );
  }, [editablePlanAmount]);

  // handleSaveTMCount:
  // Total TM Count is edited in the Subscription Summary (per-attorney counts are
  // read-only). The calculation effect doesn't depend on totalTMCount, so the
  // amounts are recomputed here — otherwise Final Amount would keep the old TMs.
  const handleSaveTMCount = (count) => {
    isTMCountManuallySet.current = true;

    let nextTotalAmount = 0;
    setOrderSummary((prev) => {
      const planAmount = Number(prev.planAmount || 0);
      const finalAmount = planAmount * count;
      const gstAmount = prev.includeGST ? finalAmount * 0.18 : 0;
      nextTotalAmount = finalAmount + gstAmount;
      return {
        ...prev,
        totalTMCount: count,
        finalAmount: Number(finalAmount).toFixed(2),
        gstAmount: Number(gstAmount).toFixed(2),
        totalAmount: Number(nextTotalAmount).toFixed(2),
      };
    });

    setFormData((prev) => ({
      ...prev,
      total_tm_count: count,
      final_amount: nextTotalAmount,
    }));

    toast.success(`Total TM count updated to ${Number(count).toLocaleString("en-IN")}`);
  };

  // Apply a TmCountSuggestion: sets the TM count AND the per-TM price together so
  // price × count lands exactly on the admin's target total. Doing it in one
  // handler matters — calling handleSaveTMCount then handleSaveAmount would read
  // a stale editablePlanAmount from the previous render and drop the new price.
  const handleApplyTmSuggestion = useCallback((newCount, perTm) => {
    isTMCountManuallySet.current = true;
    setIsEditingAmount(false);
    setEditablePlanAmount(perTm);

    let nextTotalAmount = 0;
    setOrderSummary((prev) => {
      const finalAmount = perTm * newCount;
      const gstAmount = prev.includeGST ? finalAmount * 0.18 : 0;
      nextTotalAmount = finalAmount + gstAmount;
      return {
        ...prev,
        totalTMCount: newCount,
        planAmount: Number(perTm).toFixed(2),
        finalAmount: Number(finalAmount).toFixed(2),
        gstAmount: Number(gstAmount).toFixed(2),
        totalAmount: Number(nextTotalAmount).toFixed(2),
      };
    });

    setFormData((prev) => ({
      ...prev,
      plan_price: perTm,
      total_tm_count: newCount,
      final_amount: nextTotalAmount,
    }));

    toast.success(
      `Applied ${Number(newCount).toLocaleString("en-IN")} TM × ₹${Number(perTm).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
    );
  }, []);

  // ==================== CUSTOMER MODAL FUNCTIONS ====================

  const generatePassword = () => {
    setIsGenerating(true);
    const generatedPassword = cryptoRandomString({
      length: 8,
      type: "alphanumeric",
    });
    setPassword(generatedPassword);
    setNewCustomer((prev) => ({
      ...prev,
      password: generatedPassword,
    }));
    setIsGenerating(false);
  };

  const handleNewCustomerChange = (e) => {
    let { name, value } = e.target;

    if (name === "mobile") {
      value = value.replace(/\D/g, "").slice(0, 10);
    }

    setNewCustomer((prev) => ({ ...prev, [name]: value }));

    const errorMsg = validateCustomerField(name, value);
    setCustomerErrors((prev) => ({ ...prev, [name]: errorMsg }));
  };

  const validateCustomerField = (name, value) => {
    let message = "";

    if (name === "name") {
      if (!value.trim()) {
        message = "Name is required.";
      } else if (!nameRegex.test(value)) {
        message = "Enter a valid name (letters only).";
      }
    } else if (name === "email") {
      if (!value.trim()) {
        message = "Email is required.";
      } else if (!emailRegex.test(value)) {
        message = "Enter a valid email address.";
      }
    } else if (name === "mobile") {
      if (!value.trim()) {
        message = "Mobile number is required.";
      } else if (!mobileRegex.test(value)) {
        message = "Enter a valid number (4-15 digits, no country code).";
      }
    } else if (name === "address") {
      if (!value.trim()) {
        message = "Address is required.";
      }
    }

    return message;
  };

  const handleAddCustomer = async (e) => {
    e.preventDefault();

    if (!password) {
      toast.warning("Password is required.");
      return;
    }

    const customerData = {
      name: newCustomer.name,
      email: newCustomer.email,
      mobile: newCustomer.mobile,
      country_code: newCustomer.country_code || "+91",
      user_type: newCustomer.user_type || "customer",
      password: password,
      state:
        statesData.find((s) => s.value === formData.state_name)?.label || "",
      pincode: formData.pincode,
      gst_inclusion: formData.gst_inclusion ? 1 : 0,
      company_name: formData.gst_inclusion ? formData.company_name : "",
      gst: formData.gst_inclusion ? formData.gstNumber : "",
      address: newCustomer.address,
    };

    addCustomerMutation.mutate(customerData);
  };

  // ==================== FORM SUBMISSION ====================

  const validateForm = () => {
    let errors = {};
    const customerType = String(formData.customer_type);

    if (!formData.user_id) errors.user_id = "select user name";
    if (!formData.plan_id) errors.plan_id = "select plan";
    if (formData.customer_type === "" || formData.customer_type === null) {
      errors.customer_type = "Please select subscription type";
    }

    if (formData.is_attorney === "1") {
      const hasExisting = isEditing && existingAttorneys.length > 0;
      const hasNewSlot = isEditing
        ? emptySlots.some((s) => s.value.trim() !== "")
        : (attorneyNumbers && attorneyNumbers.length > 0 && !attorneyNumbers.every((num) => !num.trim()));
      if (!hasExisting && !hasNewSlot) {
        errors.attorney_details = "Please provide at least one attorney number";
      }
    }

    if (formData.is_attorney === "1") {
      if (!isEditing) {
        const unselected = attorneyNumbers
          .filter((num) => num.trim() && attorneyNameOptions[num]?.length > 0 && !(selectedAttorneyNames[num]?.length > 0))
          .map((num) => num);
        if (unselected.length > 0) {
          errors.attorney_name = `Please select an attorney name for attorney number(s): ${unselected.join(", ")}`;
        }
      } else {
        const unselectedExisting = existingAttorneys
          .filter((a) => {
            const num = String(a.attorney_number);
            return attorneyNameOptions[num]?.length > 0 && !(selectedAttorneyNames[num]?.length > 0);
          })
          .map((a) => a.attorney_number);
        const unselectedNew = emptySlots
          .filter(
            (s) =>
              s.status === "valid" &&
              s.value.trim() &&
              attorneyNameOptions[s.value.trim()]?.length > 0 &&
              !(selectedAttorneyNames[s.value.trim()]?.length > 0),
          )
          .map((s) => s.value.trim());
        const allUnselected = [...unselectedExisting, ...unselectedNew];
        if (allUnselected.length > 0) {
          errors.attorney_name = `Please select an attorney name for attorney number(s): ${allUnselected.join(", ")}`;
        }
      }
    }

    if (formData.payment_status === "paid") {
      if (!formData.payment_mode) {
        errors.payment_mode = "Please select payment mode";
      }

      if (
        (formData.payment_mode === "upi" ||
          formData.payment_mode === "bank_transfer") &&
        !formData.utr_no
      ) {
        errors.utr_no = "UTR Number is required";
      }

      if (formData.payment_mode === "bank_transfer" && !formData.bank_name) {
        errors.bank_name = "Please select bank";
      }

      if (!formData.payment_attachment) {
        errors.payment_attachment = "Attachment is required";
      }
    }

    if (formData.is_attorney === "" || formData.is_attorney === null) {
      errors.is_attorney = "Please select plan type";
    }

    if (customerType === "1") {
      if (
        !formData.plan_type ||
        formData.plan_type === "" ||
        formData.plan_type === "null"
      ) {
        errors.plan_type = "Please select plan duration";
      }
    }

    if (formData.customer_type == 2 && !formData.end_date) {
      errors.end_date = "Please select end date for free trial";
    }
    return errors;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      const firstError = Object.values(errors)[0];
      toast.warning(firstError);
      setErrors(errors);
      return;
    }

    const payload = new FormData();

    payload.append("user_id", formData.user_id);
    if (originLeadId) payload.append("lead_id", originLeadId);
    payload.append("plan_id", formData.plan_id);
    payload.append("total_amount", Number(orderSummary.totalAmount));
    payload.append("plan_price", Number(formData.plan_price));
    payload.append("total_tm_count", orderSummary.totalTMCount);
    payload.append(
      "plan_type",
      formData.customer_type === "2" ? "free_trial" : formData.plan_type,
    );
    payload.append("is_attorney", Number(formData.is_attorney));

    // Build attorney_details: in edit mode combine locked existing + valid new slots
    const allAttorneyDetails = isEditing
      ? [
          ...existingAttorneys.map((a) => ({
            attorney_number: a.attorney_number,
            tm_count: a.tm_count || null,
            attorney_names: selectedAttorneyNames[String(a.attorney_number)] || [],
          })),
          ...emptySlots
            .filter((s) => s.status === "valid" && s.value.trim())
            .map((s) => ({
              attorney_number: s.value.trim(),
              tm_count: s.tmCount || null,
              attorney_names: selectedAttorneyNames[s.value.trim()] || [],
            })),
        ]
      : attorneyNumbers
          .filter((n) => n.trim())
          .map((num) => ({
            attorney_number: num.trim(),
            tm_count: attorneyTMCounts[num.trim()] ?? null,
            attorney_names: selectedAttorneyNames[num.trim()] || [],
          }));

    payload.append("attorney_details", JSON.stringify(allAttorneyDetails));
    payload.append("customer_type", Number(formData.customer_type));
    // payload.append(
    //   "free_trial_up_to",
    //   formData.customer_type === "2" ? formData.end_date : "",
    // );
    if (formData.customer_type === "2" && formData.end_date) {
      const parsed = dayjs(formData.end_date, [
        "DD-MM-YYYY",
        "DD/MM/YYYY",
        "YYYY-MM-DD",
      ]);
      payload.append(
        "free_trial_up_to",
        parsed.isValid() ? parsed.format("DD-MM-YYYY") : formData.end_date,
      );
    } else {
      payload.append("free_trial_up_to", "");
    }
    payload.append("gst_inclusion", orderSummary.includeGST ? 1 : 0);
    payload.append("gst_amount", Number(orderSummary.gstAmount));
    payload.append("total_payable", Number(orderSummary.totalAmount));

    if (formData.customer_type === "2" && formData.end_date) {
      const days = dayjs(formData.end_date, "DD-MM-YYYY").diff(
        dayjs().startOf("day"),
        "day",
      );
      payload.append("free_trial_duration", days > 0 ? days : 0);
    }

    // Optional custom end date for paid orders
    if (formData.customer_type === "1" && formData.end_date) {
      const parsed = dayjs(formData.end_date, ["DD/MM/YYYY", "DD-MM-YYYY", "YYYY-MM-DD"]);
      payload.append(
        "subscription_end_date",
        parsed.isValid() ? parsed.format("DD/MM/YYYY") : formData.end_date,
      );
    }

    // PAYMENT LOGIC
    if (formData.customer_type === "2") {
      // Free trial: no payment collected
      payload.append("payment_status", 0);
    } else if (formData.payment_status === "manual") {
      // Offline/manual paid
      payload.append("payment_status", 0);
      payload.append("payment_mode", formData.payment_mode || "");
      payload.append("bank_name", formData.bank_name || "");
      payload.append("utr", formData.utr_no || "");
      payload.append("amount_received", formData.amount_received || 0);
      if (formData.payment_attachment) {
        payload.append("payment_proof", formData.payment_attachment);
      }
    } else {
      // "link" — online payment: send payment_status: 2 → backend returns Cashfree link
      payload.append("payment_status", 2);
    }

    // ================= SUBMIT =================
    setIsSubmitting(true);
    createOrderMutation.mutate(payload, {
      onSettled: () => {
        setIsSubmitting(false);
      },
    });
  };

  const planOption = useMemo(() => {
    if (!Array.isArray(activePlans)) return [];
    return activePlans.map((item) => ({
      value: item.id,
      label: item.name,
    }));
  }, [activePlans]);

  const planTypeOptions = [
    { value: "1", label: "Attorney/IP Professional" },
    { value: "0", label: "Corporates" },
  ];

  const customerTypeOptions = [
    { value: "2", label: "Free Trial" },
    { value: "1", label: "Paid" },
  ];

  const in_Menu_Options = [
    { value: "monthly", label: "Monthly" },
    { value: "annualy", label: "Annualy" },
  ];

  return (
    <>
      <div className="main-content">
        <div className="page-content">
          <div className="container-fluid">
            {/* Breadcrumb */}
            <div className="row">
              <div className="col-12">
                <div className="page-title-box d-sm-flex align-items-center justify-content-between">
                  <h4 className="mb-sm-0 font-size-18">
                    {isEditing ? "Edit Subscription" : "Create Subscription"}
                  </h4>
                  <div className="page-title-right">
                    <ol className="breadcrumb m-0">
                      <li className="breadcrumb-item">
                        <Link to="/">CMS</Link>
                      </li>
                      <li className="breadcrumb-item">
                        <Link to="/cms/manage-customer/manage-order">
                          Manage Subscription
                        </Link>
                      </li>
                      <li className="breadcrumb-item active">
                        {isEditing
                          ? "Edit Subscription"
                          : "Create Subscription"}
                      </li>
                    </ol>
                  </div>
                </div>
              </div>
            </div>

            <div className="row">
              {/* Left Column - Form */}
              <div className="col-lg-8 col-md-12">
                <Card className="shadow-sm">
                  <div className="bg-light p-3 border-bottom">
                    <Title level={4} className="mb-0">
                      <FileTextOutlined className="me-2" />
                      {isEditing ? "Edit Subscription" : "Create Subscription"}
                    </Title>
                  </div>
                  <div className="card-body" style={{ overflow: "hidden" }}>
                    <Spin spinning={isPageLoading}>
                      <form ref={formRef} onSubmit={handleSubmit}>
                        <Row gutter={[16, 16]}>
                          <CustomerDetailsSection
                            formData={formData}
                            setFormData={setFormData}
                            customerList={customersData}
                            isLoading={customersLoading}
                            errors={errors}
                            setErrors={setErrors}
                            calculateOrderSummary={calculateOrderSummary}
                            setOrderSummary={setOrderSummary}
                            customerTypeOptions={customerTypeOptions}
                            planTypeOptions={planTypeOptions}
                            handleCustomerTypeChange={handleCustomerTypeChange}
                            handleCustomerPaymentTypeChange={
                              handleCustomerPaymentTypeChange
                            }
                            setOpenAddCustomer={setOpenAddCustomer}
                            isCustomerPreSelected={isCustomerPreSelected}
                          />

                          <PlanDetailsSection
                            formData={formData}
                            errors={errors}
                            planOption={planOption}
                            in_Menu_Options={in_Menu_Options}
                            handleSelect={handleSelect}
                            handlePlanTypeChange={handlePlanTypeChange}
                            handleInputChange={handleInputChange}
                            setOrderSummary={setOrderSummary}
                          />

                          <PaymentDetailsSection
                            formData={formData}
                            setFormData={setFormData}
                            errors={errors}
                            // paymentModes={paymentModes}
                            handleSelect={handleSelect}
                            bankOptions={bankOptions}
                            banksLoading={banksLoading}
                          />

                          <AttorneyNumbersSection
                            attorneyCount={attorneyCount}
                            attorneyNumbers={attorneyNumbers}
                            attorneyValidation={attorneyValidation}
                            attorneyValidationMessages={
                              attorneyValidationMessages
                            }
                            attorneyTMCounts={attorneyTMCounts}
                            isCheckingAttorney={isCheckingAttorney}
                            handleAttorneyNumberChange={
                              handleAttorneyNumberChange
                            }
                            handleCheckAttorneyStatus={handleAttorneyCheckClick}
                            isAttorney={formData.is_attorney}
                            // Corporate TM count — the plan's own figure, or the
                            // fixed trial sample. The rep can still change it
                            // from Subscription Summary → Total TM Count.
                            corporateTMCount={orderSummary.totalTMCount}
                            isFreeTrial={formData.customer_type === "2"}
                            corporatePlanName={orderSummary.planName}
                            // Edit mode props
                            isEditing={isEditing}
                            existingAttorneys={existingAttorneys}
                            emptySlots={emptySlots}
                            planLimit={editPlanLimit}
                            onSlotInputChange={handleSlotInputChange}
                            onCheckSlotStatus={handleCheckSlotStatus}
                            // Name selection props
                            attorneyNameOptions={attorneyNameOptions}
                            selectedAttorneyNames={selectedAttorneyNames}
                            onSelectAttorneyName={(num, name) => {
                              setSelectedAttorneyNames((prev) => {
                                const current = prev[num] || [];
                                const exists = current.includes(name);
                                const updated = exists
                                  ? current.filter((n) => n !== name)
                                  : [...current, name];
                                return { ...prev, [num]: updated };
                              });
                            }}
                            onTMCountChange={handleTMCountChange}
                          />
                        </Row>
                      </form>
                    </Spin>
                  </div>
                </Card>
              </div>

              {/* Right Column - Order Summary */}
              <div className="col-lg-4 col-md-12">
                <OrderSummary
                  orderSummary={orderSummary}
                  formData={formData}
                  isEditing={isEditing}
                  isEditingAmount={isEditingAmount}
                  editablePlanAmount={editablePlanAmount}
                  setEditablePlanAmount={setEditablePlanAmount}
                  handleEditAmount={handleEditAmount}
                  handleSaveAmount={handleSaveAmount}
                  setIsEditingAmount={setIsEditingAmount}
                  safeNumber={safeNumber}
                  handleGSTCheckboxChange={handleGSTCheckboxChange}
                  onSaveTMCount={handleSaveTMCount}
                  onApplyTmSuggestion={handleApplyTmSuggestion}
                  isSubmitting={isSubmitting}
                  formRef={formRef}
                  // Edit mode attorney props
                  existingAttorneys={existingAttorneys}
                  emptySlots={emptySlots}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Add Customer Modal */}
      <AddCustomerModal
        open={openAddCustomer}
        onCancel={() => setOpenAddCustomer(false)}
        onSubmit={handleAddCustomer}
        newCustomer={newCustomer}
        setNewCustomer={setNewCustomer}
        password={password}
        setPassword={setPassword}
        isGenerating={isGenerating}
        generatePassword={generatePassword}
        handleNewCustomerChange={handleNewCustomerChange}
        states={statesData}
        errors={errors}
        customerErrors={customerErrors}
        formData={formData}
        setFormData={setFormData}
        isAddingCustomer={addCustomerMutation.isPending}
      />
    </>
  );
};

export default CreateOrder;
